// components/QuantumWorkflowOrchestrator/QuantumWorkflowOrchestrator.jsx
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ReactFlow, {
  ReactFlowProvider,
  Controls,
  ControlButton,
  Background,
  MiniMap,
  Panel,
  addEdge,
  useNodesState,
  useEdgesState,
  MarkerType,
  ConnectionLineType,
  useReactFlow,
} from 'reactflow';
import 'reactflow/dist/style.css';

// Import lifecycle database
import { LIFECYCLE_DATABASE } from '../../data/lifecycles';

// Enhanced MUI Components
import {
  Container, Typography, Paper, Card, CardContent, CardActions,
  Button, IconButton, Chip, Avatar, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions, Slider, ToggleButton, ToggleButtonGroup,
  TextField, InputAdornment, Menu, MenuItem, List, ListItem,
  ListItemIcon, ListItemText, Divider, Badge, CircularProgress,
  LinearProgress, Alert, Snackbar, Fade, Zoom, Grow, Slide,
  Stepper, Step, StepLabel, Fab, SpeedDial, SpeedDialAction,
  SpeedDialIcon, Drawer, AppBar, Toolbar, Tabs, Tab,
  FormControl, InputLabel, Select, Switch, FormControlLabel,
  RadioGroup, Radio, Checkbox, Accordion, AccordionSummary,
  AccordionDetails, Rating, Autocomplete, Skeleton,
  Breadcrumbs, Pagination, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow,
  Timeline, TimelineItem, TimelineSeparator,
  TimelineDot, TimelineConnector, TimelineContent,
  TimelineOppositeContent,
  Grid,
  ListItemSecondaryAction,
  Box
} from '@mui/material';

// MUI Icons - Individual imports for stability
import Analytics from '@mui/icons-material/Analytics';
import RocketLaunch from '@mui/icons-material/RocketLaunch';
import AutoAwesome from '@mui/icons-material/AutoAwesome';
import Bolt from '@mui/icons-material/Bolt';
import TrendingUp from '@mui/icons-material/TrendingUp';
import Science from '@mui/icons-material/Science';
import Psychology from '@mui/icons-material/Psychology';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Warning from '@mui/icons-material/Warning';
import Shield from '@mui/icons-material/Shield';
import Refresh from '@mui/icons-material/Refresh';
import Link from '@mui/icons-material/Link';
import Architecture from '@mui/icons-material/Architecture';
import ExpandMore from '@mui/icons-material/ExpandMore';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import Settings from '@mui/icons-material/Settings';
import Cloud from '@mui/icons-material/Cloud';
import Engineering from '@mui/icons-material/Engineering';
import Book from '@mui/icons-material/Book';
import Search from '@mui/icons-material/Search';
import Dashboard from '@mui/icons-material/Dashboard';
import AccountTree from '@mui/icons-material/AccountTree';
import Save from '@mui/icons-material/Save';
import RadioIcon from '@mui/icons-material/Radio';
import Add from '@mui/icons-material/Add';
import Share from '@mui/icons-material/Share';
import Fullscreen from '@mui/icons-material/Fullscreen';
import FullscreenExit from '@mui/icons-material/FullscreenExit';
import ChevronRight from '@mui/icons-material/ChevronRight';
import Close from '@mui/icons-material/Close';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import ContentCopy from '@mui/icons-material/ContentCopy';
import Help from '@mui/icons-material/Help';
import PlayArrow from '@mui/icons-material/PlayArrow';
import Stop from '@mui/icons-material/Stop';
import Pause from '@mui/icons-material/Pause';
import ZoomIn from '@mui/icons-material/ZoomIn';
import ZoomOut from '@mui/icons-material/ZoomOut';
import FitScreen from '@mui/icons-material/FitScreen';
import Download from '@mui/icons-material/Download';
import Upload from '@mui/icons-material/Upload';

// Framer Motion for advanced animations
import { motion, AnimatePresence, useAnimation } from 'framer-motion';

// Styled Components with Emotion
import styled from '@emotion/styled';
import { keyframes, css } from '@emotion/react';

// ==================== QUANTUM CONSTANTS & THEMES ====================
const QUANTUM_STATES = {
  COHERENT: 'coherent',
  SUPERPOSITION: 'superposition',
  ENTANGLED: 'entangled',
  INTERFERENCE: 'interference',
  DECOHERENT: 'decoherent'
};

const QUANTUM_COLORS = {
  [QUANTUM_STATES.COHERENT]: '#10B981',
  [QUANTUM_STATES.SUPERPOSITION]: '#8B5CF6',
  [QUANTUM_STATES.ENTANGLED]: '#3B82F6',
  [QUANTUM_STATES.INTERFERENCE]: '#F59E0B',
  [QUANTUM_STATES.DECOHERENT]: '#EF4444'
};

const LIFECYCLE_CATEGORY_COLORS = {
  EXISTENTIAL: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  FOUNDATIONAL: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  STRATEGIC: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  OPERATIONAL: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
  ENGINEERING: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
  DIGITAL: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
  COMPLIANCE: 'linear-gradient(135deg, #d299c2 0%, #fef9d7 100%)',
  ENABLING: 'linear-gradient(135deg, #89f7fe 0%, #66a6ff 100%)',
  EVOLUTIONARY: 'linear-gradient(135deg, #f6d365 0%, #fda085 100%)',
  KNOWLEDGE: 'linear-gradient(135deg, #a6c0fe 0%, #f68084 100%)',
  CUSTOM: 'linear-gradient(135deg, #6ee7b7 0%, #3b82f6 100%)'
};

// ==================== LAYOUT ENGINE ====================
class QuantumLayoutEngine {
  constructor() {
    this.layouts = {
      CIRCULAR: 'circular',
      HIERARCHICAL: 'hierarchical',
      FORCE_DIRECTED: 'force_directed',
      RADIAL: 'radial',
      TREE: 'tree',
      GRID: 'grid',
      SPIRAL: 'spiral',
      CONCENTRIC: 'concentric',
      COSMIC: 'cosmic',
      QUANTUM_WAVE: 'quantum_wave'
    };
  }

  applyLayout(nodes, edges, layoutType, canvasWidth, canvasHeight) {
    const centerX = canvasWidth / 2;
    const centerY = canvasHeight / 2;
    const radius = Math.min(canvasWidth, canvasHeight) * 0.35;
    
    let newNodes = [...nodes];
    
    switch(layoutType) {
      case this.layouts.CIRCULAR:
        return this.circularLayout(newNodes, centerX, centerY, radius);
        
      case this.layouts.HIERARCHICAL:
        return this.hierarchicalLayout(newNodes, edges, canvasWidth, canvasHeight);
        
      case this.layouts.FORCE_DIRECTED:
        return this.forceDirectedLayout(newNodes, edges, canvasWidth, canvasHeight);
        
      case this.layouts.RADIAL:
        return this.radialLayout(newNodes, edges, centerX, centerY, radius);
        
      case this.layouts.TREE:
        return this.treeLayout(newNodes, edges, canvasWidth, canvasHeight);
        
      case this.layouts.GRID:
        return this.gridLayout(newNodes, canvasWidth, canvasHeight);
        
      case this.layouts.SPIRAL:
        return this.spiralLayout(newNodes, centerX, centerY);
        
      case this.layouts.CONCENTRIC:
        return this.concentricLayout(newNodes, edges, centerX, centerY);
        
      case this.layouts.COSMIC:
        return this.cosmicLayout(newNodes, centerX, centerY);
        
      case this.layouts.QUANTUM_WAVE:
        return this.quantumWaveLayout(newNodes, centerX, centerY);
        
      default:
        return newNodes;
    }
  }

  // 1. CIRCULAR LAYOUT
  circularLayout(nodes, centerX, centerY, radius) {
    const angleStep = (2 * Math.PI) / nodes.length;
    
    return nodes.map((node, index) => ({
      ...node,
      position: {
        x: centerX + radius * Math.cos(index * angleStep),
        y: centerY + radius * Math.sin(index * angleStep)
      }
    }));
  }

  // 2. HIERARCHICAL LAYOUT
  hierarchicalLayout(nodes, edges, width, height) {
    // Find root nodes (no incoming edges)
    const rootNodes = nodes.filter(node => 
      !edges.some(edge => edge.target === node.id)
    );
    
    const levelHeight = height / (rootNodes.length + 2);
    const nodeWidth = width / 3;
    
    return nodes.map((node, index) => {
      const isRoot = rootNodes.some(root => root.id === node.id);
      const rootIndex = rootNodes.findIndex(root => root.id === node.id);
      
      if (isRoot) {
        return {
          ...node,
          position: {
            x: nodeWidth * 1.5,
            y: levelHeight * (rootIndex + 1)
          }
        };
      } else {
        // Find parent node
        const incomingEdge = edges.find(edge => edge.target === node.id);
        const parentNode = incomingEdge ? nodes.find(n => n.id === incomingEdge.source) : null;
        const parentRootIndex = parentNode ? rootNodes.findIndex(root => root.id === parentNode.id) : 0;
        
        // Count children at same level
        const siblingEdges = edges.filter(edge => edge.source === (parentNode?.id || ''));
        const siblingIndex = siblingEdges.findIndex(edge => edge.target === node.id);
        
        return {
          ...node,
          position: {
            x: nodeWidth * 2.5,
            y: levelHeight * (parentRootIndex + 1) + (siblingIndex * 50)
          }
        };
      }
    });
  }

  // 3. FORCE-DIRECTED LAYOUT
  forceDirectedLayout(nodes, edges, width, height) {
    const k = Math.sqrt((width * height) / nodes.length);
    const temperature = width / 10;
    const maxIterations = 100;
    
    // Initialize positions with random spread
    let positions = nodes.map(node => ({
      id: node.id,
      x: Math.random() * width,
      y: Math.random() * height,
      vx: 0,
      vy: 0
    }));
    
    // Force simulation
    for (let iteration = 0; iteration < maxIterations; iteration++) {
      // Calculate repulsive forces
      positions.forEach((p1, i) => {
        positions.forEach((p2, j) => {
          if (i !== j) {
            const dx = p1.x - p2.x;
            const dy = p1.y - p2.y;
            const distance = Math.sqrt(dx * dx + dy * dy) || 1;
            
            // Repulsive force
            const force = (k * k) / distance;
            p1.vx += (dx / distance) * force;
            p1.vy += (dy / distance) * force;
          }
        });
      });
      
      // Calculate attractive forces from edges
      edges.forEach(edge => {
        const sourcePos = positions.find(p => p.id === edge.source);
        const targetPos = positions.find(p => p.id === edge.target);
        
        if (sourcePos && targetPos) {
          const dx = sourcePos.x - targetPos.x;
          const dy = sourcePos.y - targetPos.y;
          const distance = Math.sqrt(dx * dx + dy * dy) || 1;
          
          // Attractive force
          const force = (distance * distance) / k;
          sourcePos.vx -= (dx / distance) * force;
          sourcePos.vy -= (dy / distance) * force;
          targetPos.vx += (dx / distance) * force;
          targetPos.vy += (dy / distance) * force;
        }
      });
      
      // Update positions with cooling
      const cool = temperature * (1 - iteration / maxIterations);
      positions.forEach(pos => {
        const forceMagnitude = Math.sqrt(pos.vx * pos.vx + pos.vy * pos.vy) || 1;
        pos.x += (pos.vx / forceMagnitude) * Math.min(forceMagnitude, cool);
        pos.y += (pos.vy / forceMagnitude) * Math.min(forceMagnitude, cool);
        
        // Keep within bounds
        pos.x = Math.max(50, Math.min(width - 50, pos.x));
        pos.y = Math.max(50, Math.min(height - 50, pos.y));
        
        // Reset velocity
        pos.vx *= 0.5;
        pos.vy *= 0.5;
      });
    }
    
    // Apply positions to nodes
    return nodes.map(node => {
      const pos = positions.find(p => p.id === node.id);
      return {
        ...node,
        position: pos ? { x: pos.x, y: pos.y } : node.position
      };
    });
  }

  // 4. RADIAL LAYOUT
  radialLayout(nodes, edges, centerX, centerY, radius) {
    // Group by connection depth from center
    const depthMap = new Map();
    const rootNodes = nodes.filter(node => 
      !edges.some(edge => edge.target === node.id)
    );
    
    rootNodes.forEach((node, index) => {
      depthMap.set(node.id, 0);
    });
    
    // Calculate depths
    let changed = true;
    while (changed) {
      changed = false;
      edges.forEach(edge => {
        const sourceDepth = depthMap.get(edge.source);
        const targetDepth = depthMap.get(edge.target);
        
        if (sourceDepth !== undefined && (targetDepth === undefined || targetDepth < sourceDepth + 1)) {
          depthMap.set(edge.target, sourceDepth + 1);
          changed = true;
        }
      });
    }
    
    // Position nodes based on depth
    const maxDepth = Math.max(...Array.from(depthMap.values()));
    
    return nodes.map((node, index) => {
      const depth = depthMap.get(node.id) || 0;
      const angle = (index * 2 * Math.PI) / nodes.length;
      const nodeRadius = radius * (0.3 + (depth / (maxDepth + 1)) * 0.7);
      
      return {
        ...node,
        position: {
          x: centerX + nodeRadius * Math.cos(angle),
          y: centerY + nodeRadius * Math.sin(angle)
        }
      };
    });
  }

  // 5. TREE LAYOUT
  treeLayout(nodes, edges, width, height) {
    const rootNodes = nodes.filter(node => 
      !edges.some(edge => edge.target === node.id)
    );
    
    const levelWidth = width / (rootNodes.length + 1);
    const levelHeight = height / 6;
    
    return nodes.map(node => {
      const isRoot = rootNodes.some(root => root.id === node.id);
      const rootIndex = rootNodes.findIndex(root => root.id === node.id);
      
      if (isRoot) {
        return {
          ...node,
          position: {
            x: levelWidth * (rootIndex + 1),
            y: levelHeight
          }
        };
      } else {
        // Find path to root
        let currentNode = node;
        let depth = 0;
        while (true) {
          const incomingEdge = edges.find(edge => edge.target === currentNode.id);
          if (!incomingEdge) break;
          currentNode = nodes.find(n => n.id === incomingEdge.source);
          depth++;
        }
        
        const parentRootIndex = rootNodes.findIndex(root => root.id === currentNode.id);
        const siblings = edges.filter(edge => edge.source === currentNode.id).length;
        const siblingIndex = edges.findIndex(edge => edge.target === node.id);
        
        return {
          ...node,
          position: {
            x: levelWidth * (parentRootIndex + 1) + ((siblingIndex - siblings/2) * 80),
            y: levelHeight * (depth + 2)
          }
        };
      }
    });
  }

  // 6. GRID LAYOUT
  gridLayout(nodes, width, height) {
    const cols = Math.ceil(Math.sqrt(nodes.length));
    const rows = Math.ceil(nodes.length / cols);
    const cellWidth = width / (cols + 1);
    const cellHeight = height / (rows + 1);
    
    return nodes.map((node, index) => {
      const row = Math.floor(index / cols);
      const col = index % cols;
      
      return {
        ...node,
        position: {
          x: cellWidth * (col + 1),
          y: cellHeight * (row + 1)
        }
      };
    });
  }

  // 7. SPIRAL LAYOUT
  spiralLayout(nodes, centerX, centerY) {
    const a = 10; // Spiral tightness
    const b = 20; // Spiral growth
    
    return nodes.map((node, index) => {
      const angle = a * index * (Math.PI / 180);
      const radius = b * angle;
      
      return {
        ...node,
        position: {
          x: centerX + radius * Math.cos(angle),
          y: centerY + radius * Math.sin(angle)
        }
      };
    });
  }

  // 8. CONCENTRIC LAYOUT
  concentricLayout(nodes, edges, centerX, centerY) {
    // Sort by number of connections
    const nodesWithConnections = nodes.map(node => {
      const connections = edges.filter(edge => 
        edge.source === node.id || edge.target === node.id
      ).length;
      return { node, connections };
    }).sort((a, b) => b.connections - a.connections);
    
    const rings = 3;
    const maxConnections = Math.max(...nodesWithConnections.map(n => n.connections));
    
    return nodesWithConnections.map(({ node, connections }, index) => {
      const ring = Math.floor((connections / maxConnections) * rings);
      const ringRadius = [100, 200, 300][ring] || 100;
      const angle = (index * 2 * Math.PI) / nodesWithConnections.length;
      
      return {
        ...node,
        position: {
          x: centerX + ringRadius * Math.cos(angle),
          y: centerY + ringRadius * Math.sin(angle)
        }
      };
    });
  }

  // 9. COSMIC LAYOUT (Star cluster)
  cosmicLayout(nodes, centerX, centerY) {
    const clusters = 3;
    const clusterRadius = 150;
    
    return nodes.map((node, index) => {
      const cluster = index % clusters;
      const angleInCluster = (index * 2 * Math.PI) / (nodes.length / clusters);
      const clusterAngle = (cluster * 2 * Math.PI) / clusters;
      
      const x = centerX + 
        clusterRadius * Math.cos(clusterAngle) + 
        80 * Math.cos(angleInCluster);
      const y = centerY + 
        clusterRadius * Math.sin(clusterAngle) + 
        80 * Math.sin(angleInCluster);
      
      return {
        ...node,
        position: { x, y }
      };
    });
  }

  // 10. QUANTUM WAVE LAYOUT (Most visually impressive)
  quantumWaveLayout(nodes, centerX, centerY) {
    const waveCount = 3;
    const waveAmplitude = 100;
    
    return nodes.map((node, index) => {
      const wave = index % waveCount;
      const wavePhase = (wave * 2 * Math.PI) / waveCount;
      const positionInWave = index / nodes.length;
      
      const x = centerX + 
        (index * 30 - nodes.length * 15) + 
        waveAmplitude * Math.sin(wavePhase + positionInWave * Math.PI * 2);
      const y = centerY + 
        wave * 100 + 
        waveAmplitude * Math.cos(wavePhase + positionInWave * Math.PI * 2);
      
      return {
        ...node,
        position: { x, y }
      };
    });
  }

  generateLayoutDescription(layoutType) {
    const descriptions = {
      [this.layouts.CIRCULAR]: "Nodes arranged in perfect circle with equal spacing",
      [this.layouts.HIERARCHICAL]: "Top-down flow showing dependencies and hierarchy",
      [this.layouts.FORCE_DIRECTED]: "Physics simulation with natural attraction/repulsion",
      [this.layouts.RADIAL]: "Root nodes at center, expanding outward radially",
      [this.layouts.TREE]: "Clear parent-child relationships in branching structure",
      [this.layouts.GRID]: "Neat organized grid for maximum clarity",
      [this.layouts.SPIRAL]: "Archimedean spiral creating mesmerizing patterns",
      [this.layouts.CONCENTRIC]: "Important nodes in center, others in concentric rings",
      [this.layouts.COSMIC]: "Star cluster formation with multiple centers",
      [this.layouts.QUANTUM_WAVE]: "Quantum wave interference pattern for maximum visual impact"
    };
    
    return descriptions[layoutType] || "Standard layout";
  }

  getLayoutIcon(layoutType) {
    const icons = {
      [this.layouts.CIRCULAR]: "🔵",
      [this.layouts.HIERARCHICAL]: "📊",
      [this.layouts.FORCE_DIRECTED]: "🌀",
      [this.layouts.RADIAL]: "🎯",
      [this.layouts.TREE]: "🌳",
      [this.layouts.GRID]: "🔳",
      [this.layouts.SPIRAL]: "🔄",
      [this.layouts.CONCENTRIC]: "⭕",
      [this.layouts.COSMIC]: "🌌",
      [this.layouts.QUANTUM_WAVE]: "⚛️"
    };
    
    return icons[layoutType] || "📐";
  }
}

// ==================== STYLED COMPONENTS ====================
const QuantumContainer = styled(Box)(({ theme, isFullscreen }) => ({
  height: isFullscreen ? '100vh' : 'calc(100vh - 64px)',
  width: '100vw',
  overflow: 'hidden',
  position: 'relative',
  background: `radial-gradient(circle at 20% 20%, 
    ${theme.palette.background.default} 0%,
    #0f172a 50%,
    #1e293b 100%)`,
  color: theme.palette.common.white,
  fontFamily: '"Inter", "Roboto", sans-serif',
  marginTop: isFullscreen ? 0 : '64px',
  transition: 'all 0.3s ease',
  
  // Edge styles
  '& .react-flow__edge-path': {
    strokeWidth: '3 !important',
  },
  '& .react-flow__edge.animated path': {
    strokeDasharray: '5 5 !important',
    animation: 'dashdraw 1s linear infinite !important',
  },
  '& .react-flow__edge-text': {
    fontWeight: 'bold !important',
    fontSize: '11px !important',
    textShadow: '0 1px 2px rgba(0,0,0,0.5) !important',
  },
  '& .react-flow__edge-textbg': {
    fill: 'rgba(15, 23, 42, 0.95) !important',
    stroke: 'rgba(255, 255, 255, 0.3) !important',
    strokeWidth: '1 !important',
    rx: '6 !important',
    ry: '6 !important',
  },
  
  // Edge selection styles
  '& .react-flow__edge.selected': {
    '& path': {
      stroke: 'rgba(255, 255, 255, 0.9) !important',
      filter: 'drop-shadow(0 0 10px rgba(255, 255, 255, 0.7)) !important'
    }
  },
  
  // Edge hover styles
  '& .react-flow__edge:hover': {
    '& path': {
      strokeWidth: '4 !important'
    }
  },
  
  // Animation keyframes
  '@keyframes dashdraw': {
    '0%': { strokeDashoffset: '0' },
    '100%': { strokeDashoffset: '20' }
  },
  
  '@keyframes circularFloat': {
    '0%, 100%': { transform: 'translateY(0px) rotate(0deg)' },
    '50%': { transform: 'translateY(-10px) rotate(2deg)' }
  },
  
  '@keyframes pulseGravity': {
    '0%, 100%': { transform: 'scale(1)' },
    '50%': { transform: 'scale(1.05)' }
  },
  
  '@keyframes quantumWave': {
    '0%, 100%': { 
      transform: 'translateX(0) translateY(0)',
      filter: 'brightness(1) drop-shadow(0 0 10px rgba(59, 130, 246, 0.5))'
    },
    '25%': { 
      transform: 'translateX(5px) translateY(-5px)',
      filter: 'brightness(1.2) drop-shadow(0 0 15px rgba(139, 92, 246, 0.7))'
    },
    '50%': { 
      transform: 'translateX(0) translateY(5px)',
      filter: 'brightness(1) drop-shadow(0 0 10px rgba(59, 130, 246, 0.5))'
    },
    '75%': { 
      transform: 'translateX(-5px) translateY(0)',
      filter: 'brightness(1.2) drop-shadow(0 0 15px rgba(139, 92, 246, 0.7))'
    }
  },
  
  '@keyframes spiralRotate': {
    '0%': { transform: 'rotate(0deg) translateX(60px) rotate(0deg)' },
    '100%': { transform: 'rotate(360deg) translateX(60px) rotate(-360deg)' }
  },
  
  '@keyframes edgeGlow': {
    '0%, 100%': { 
      filter: 'drop-shadow(0 0 6px rgba(255,255,255,0.3))',
      strokeWidth: '2px'
    },
    '50%': { 
      filter: 'drop-shadow(0 0 12px rgba(255,255,255,0.7))',
      strokeWidth: '3px'
    }
  },
  
  '@keyframes drawEdge': {
    'to': {
      strokeDashoffset: '0'
    }
  },
  
  '@keyframes layoutReveal': {
    '0%': { 
      opacity: '0',
      transform: 'scale(0.8) rotate(-10deg)'
    },
    '100%': { 
      opacity: '1',
      transform: 'scale(1) rotate(0deg)'
    }
  },
  
  '@keyframes pulseHighlight': {
    '0%, 100%': { 
      transform: 'scale(1)',
      boxShadow: '0 10px 40px rgba(255, 255, 255, 0.2)'
    },
    '50%': { 
      transform: 'scale(1.1)',
      boxShadow: '0 20px 60px rgba(255, 255, 255, 0.5)'
    }
  }
}));

const QuantumBackground = styled('div')(() => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  overflow: 'hidden',
  zIndex: 0,
  pointerEvents: 'none',
}));

const QuantumParticle = styled(motion.div)(({ color, size }) => ({
  position: 'absolute',
  borderRadius: '50%',
  background: `radial-gradient(circle, ${color} 0%, transparent 70%)`,
  filter: 'blur(20px)',
  width: size,
  height: size,
  pointerEvents: 'none',
}));

const QuantumGrid = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundImage: `
    linear-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.05) 1px, transparent 1px)
  `,
  backgroundSize: '50px 50px',
  animation: 'gridMove 20s linear infinite',
  '@keyframes gridMove': {
    '0%': { transform: 'translate(0, 0)' },
    '100%': { transform: 'translate(50px, 50px)' }
  }
}));

const QuantumNode = styled(motion.div)(({ category, quantumState, theme }) => ({
  position: 'relative',
  padding: '20px',
  borderRadius: '16px',
  background: `
    linear-gradient(135deg, 
      rgba(255, 255, 255, 0.1) 0%,
      rgba(255, 255, 255, 0.05) 100%
    )`,
  backdropFilter: 'blur(20px)',
  border: `2px solid ${QUANTUM_COLORS[quantumState]}`,
  boxShadow: `0 10px 40px ${QUANTUM_COLORS[quantumState]}40`,
  minWidth: '280px',
  maxWidth: '350px',
  cursor: 'grab',
  '&:hover': {
    boxShadow: `0 20px 60px ${QUANTUM_COLORS[quantumState]}60`,
    transform: 'translateY(-4px)',
  },
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: LIFECYCLE_CATEGORY_COLORS[category] || QUANTUM_COLORS[quantumState],
    borderTopLeftRadius: '16px',
    borderTopRightRadius: '16px',
  }
}));

const QuantumDashboard = styled(Paper)(({ theme }) => ({
  background: `linear-gradient(135deg, 
    rgba(255, 255, 255, 0.1) 0%,
    rgba(255, 255, 255, 0.05) 100%)`,
  backdropFilter: 'blur(30px)',
  border: `1px solid rgba(255, 255, 255, 0.1)`,
  borderRadius: '24px',
  padding: '24px',
  height: '100%',
  overflow: 'hidden',
}));

// ==================== CUSTOM NODE COMPONENTS ====================
const LifecycleNode = ({ data }) => {
  const { label, category, quantumState, impactWeight, phases, connectedThreads, isCustom } = data;
  const controls = useAnimation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [localQuantumState, setLocalQuantumState] = useState(quantumState);

  const handleMouseEnter = () => {
    controls.start({
      scale: 1.05,
      rotate: 2,
      transition: { type: 'spring', stiffness: 300, damping: 20 }
    });
  };

  const handleMouseLeave = () => {
    controls.start({
      scale: 1,
      rotate: 0,
      transition: { type: 'spring', stiffness: 300, damping: 20 }
    });
  };

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <QuantumNode
      category={category}
      quantumState={localQuantumState}
      animate={controls}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight="700" sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            {label}
            {isCustom && (
              <Chip 
                label="CUSTOM" 
                size="small" 
                sx={{ 
                  background: QUANTUM_COLORS.SUPERPOSITION,
                  color: 'white',
                  fontSize: '0.6rem',
                  height: '20px'
                }}
              />
            )}
          </Typography>
          <Chip 
            label={category} 
            size="small" 
            sx={{ 
              background: LIFECYCLE_CATEGORY_COLORS[category],
              color: 'white',
              fontWeight: '600',
              fontSize: '0.7rem'
            }}
          />
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ 
            width: '12px', 
            height: '12px', 
            borderRadius: '50%',
            background: QUANTUM_COLORS[localQuantumState],
            animation: localQuantumState === QUANTUM_STATES.SUPERPOSITION ? 'pulse 2s infinite' : 'none'
          }} />
          <Typography variant="caption" sx={{ opacity: 0.8 }}>
            {localQuantumState.toUpperCase()}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ mb: 2 }}>
        <LinearProgress 
          variant="determinate" 
          value={impactWeight * 100} 
          sx={{ 
            height: '6px', 
            borderRadius: '3px',
            background: 'rgba(255, 255, 255, 0.1)',
            '& .MuiLinearProgress-bar': {
              background: QUANTUM_COLORS[localQuantumState],
              borderRadius: '3px'
            }
          }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
          <Typography variant="caption" sx={{ opacity: 0.7 }}>
            Impact
          </Typography>
          <Typography variant="caption" fontWeight="600">
            {(impactWeight * 100).toFixed(1)}%
          </Typography>
        </Box>
      </Box>

      {connectedThreads && connectedThreads.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
          {connectedThreads.slice(0, 4).map(thread => (
            <Chip
              key={thread}
              label={`T${thread}`}
              size="small"
              variant="outlined"
              sx={{ 
                fontSize: '0.65rem', 
                borderColor: QUANTUM_COLORS[localQuantumState] + '40',
                color: QUANTUM_COLORS[localQuantumState]
              }}
            />
          ))}
          {connectedThreads.length > 4 && (
            <Chip
              label={`+${connectedThreads.length - 4}`}
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.65rem' }}
            />
          )}
        </Box>
      )}

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Divider sx={{ my: 2, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
            
            {phases && phases.length > 0 && (
              <>
                <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 1 }}>
                  Phases
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                  {phases.slice(0, 3).map((phase, index) => (
                    <Chip
                      key={index}
                      label={phase}
                      size="small"
                      sx={{ 
                        fontSize: '0.7rem',
                        background: `rgba(255, 255, 255, 0.1)`
                      }}
                    />
                  ))}
                </Box>
              </>
            )}

            <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 1 }}>
              Quantum Metrics
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
              <Box>
                <Typography variant="caption" sx={{ opacity: 0.7 }}>
                  Coherence
                </Typography>
                <Typography variant="caption" fontWeight="600" display="block">
                  {(Math.random() * 100).toFixed(1)}%
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ opacity: 0.7 }}>
                  Entanglement
                </Typography>
                <Typography variant="caption" fontWeight="600" display="block">
                  {connectedThreads?.length || 0}
                </Typography>
              </Box>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
        <Button
          size="small"
          onClick={toggleExpand}
          startIcon={isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          sx={{ color: QUANTUM_COLORS[localQuantumState] }}
        >
          {isExpanded ? 'Less' : 'More'}
        </Button>
        
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Analyze">
            <IconButton size="small">
              <Analytics sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Connect">
            <IconButton size="small">
              <Link sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </QuantumNode>
  );
};

// ==================== QUANTUM AI ENGINE ====================
class QuantumAIEngine {
  constructor() {
    this.quantumWeights = new Map();
    this.lifecycleDB = LIFECYCLE_DATABASE || {};
  }

  async predictOptimalPath(workflowState, constraints) {
    return this.generateRecommendations([], workflowState);
  }

  generateRecommendations(predictions, workflowState) {
    const recommendations = [];
    const lifecycles = [...Object.values(this.lifecycleDB), ...(workflowState.customLifecycles || [])];
    
    if (lifecycles.length > 0) {
      lifecycles.slice(0, 5).forEach((lifecycle, index) => {
        const score = 0.3 + Math.random() * 0.5;
        if (score > 0.7) {
          recommendations.push({
            lifecycle: lifecycle.id,
            name: lifecycle.name,
            confidence: score,
            action: 'INTEGRATE_NOW',
            reason: `High quantum alignment (${(score * 100).toFixed(1)}%)`,
            impact: lifecycle.impactWeight * 100,
            dependencies: lifecycle.partners,
            implementationSteps: this.generateImplementationSteps(lifecycle)
          });
        } else if (score > 0.4) {
          recommendations.push({
            lifecycle: lifecycle.id,
            name: lifecycle.name,
            confidence: score,
            action: 'CONSIDER_INTEGRATION',
            reason: `Moderate alignment (${(score * 100).toFixed(1)}%)`,
            prerequisites: this.identifyPrerequisites(lifecycle),
            timeline: '2-4 weeks'
          });
        }
      });
    }

    return recommendations.sort((a, b) => b.confidence - a.confidence);
  }

  // ==================== SEQUENCING METHODS ====================
  analyzeLifecycleSequence(nodes, edges) {
    if (!nodes || nodes.length === 0) {
      return {
        sequenceLevel: 0,
        startNodes: [],
        endNodes: [],
        nodesByCategory: {},
        totalNodes: 0,
        totalConnections: edges?.length || 0,
        connectivityRatio: 0,
        hasLinearPath: false,
        recommendations: []
      };
    }

    const startNodes = nodes.filter(node => {
      return !edges?.some(edge => edge.target === node.id);
    });

    const endNodes = nodes.filter(node => {
      return !edges?.some(edge => edge.source === node.id);
    });

    const nodesByCategory = nodes.reduce((acc, node) => {
      const category = node.data?.category || 'UNCATEGORIZED';
      if (!acc[category]) acc[category] = [];
      acc[category].push({
        id: node.id,
        name: node.data?.label || 'Unnamed',
        connections: edges?.filter(e => e.source === node.id || e.target === node.id).length || 0
      });
      return acc;
    }, {});

    const maxPossibleEdges = nodes.length * (nodes.length - 1);
    const actualEdges = edges?.length || 0;
    const connectivityRatio = maxPossibleEdges > 0 ? actualEdges / maxPossibleEdges : 0;

    const hasLinearPath = this.checkForLinearPath(nodes, edges);

    let sequenceLevel = 0;
    if (connectivityRatio > 0.6 && hasLinearPath) sequenceLevel = 3;
    else if (connectivityRatio > 0.3) sequenceLevel = 2;
    else if (connectivityRatio > 0.1) sequenceLevel = 1;

    return {
      sequenceLevel,
      startNodes: startNodes.map(n => ({ id: n.id, name: n.data?.label })),
      endNodes: endNodes.map(n => ({ id: n.id, name: n.data?.label })),
      nodesByCategory,
      totalNodes: nodes.length,
      totalConnections: actualEdges,
      connectivityRatio: parseFloat(connectivityRatio.toFixed(2)),
      hasLinearPath,
      recommendations: this.generateSequenceRecommendations(nodes, edges, startNodes, endNodes)
    };
  }

  checkForLinearPath(nodes, edges) {
    if (!nodes || nodes.length === 0 || !edges || edges.length === 0) return false;

    const startNodes = nodes.filter(node => !edges.some(edge => edge.target === node.id));
    const endNodes = nodes.filter(node => !edges.some(edge => edge.source === node.id));

    if (startNodes.length === 0 || endNodes.length === 0) return false;

    const findPath = (currentId, visited = new Set()) => {
      if (visited.has(currentId)) return false;
      visited.add(currentId);

      const node = nodes.find(n => n.id === currentId);
      if (!node) return false;

      if (endNodes.some(e => e.id === currentId)) return true;

      const outgoingEdges = edges.filter(e => e.source === currentId);
      
      for (const edge of outgoingEdges) {
        if (findPath(edge.target, new Set(visited))) {
          return true;
        }
      }

      return false;
    };

    return startNodes.some(startNode => findPath(startNode.id));
  }

  generateSequenceRecommendations(nodes, edges, startNodes, endNodes) {
    const recommendations = [];
    
    const currentEdges = edges?.length || 0;
    const maxPossible = nodes.length * (nodes.length - 1);
    const connectionRatio = maxPossible > 0 ? currentEdges / maxPossible : 0;
    
    if (connectionRatio < 0.3) {
      recommendations.push({
        type: 'CONNECTIVITY',
        priority: 'HIGH',
        message: `Low connectivity (${(connectionRatio * 100).toFixed(1)}%). Consider adding more connections between lifecycles.`,
        action: 'ADD_CONNECTIONS'
      });
    }

    if (startNodes.length === 0) {
      recommendations.push({
        type: 'STRUCTURE',
        priority: 'HIGH',
        message: 'No start nodes found. Every workflow should have at least one starting point.',
        action: 'IDENTIFY_START_NODES'
      });
    }

    if (endNodes.length === 0) {
      recommendations.push({
        type: 'STRUCTURE',
        priority: 'HIGH',
        message: 'No end nodes found. Every workflow should have at least one completion point.',
        action: 'IDENTIFY_END_NODES'
      });
    }

    const connectionSuggestions = this.generateConnectionSuggestions(nodes, edges);
    if (connectionSuggestions.length > 0) {
      recommendations.push({
        type: 'OPTIMIZATION',
        priority: 'MEDIUM',
        message: `Found ${connectionSuggestions.length} potential connections based on lifecycle partnerships.`,
        action: 'SUGGEST_CONNECTIONS',
        suggestions: connectionSuggestions
      });
    }

    return recommendations;
  }

  generateConnectionSuggestions(nodes, edges = []) {
    const suggestions = [];
    
    if (!nodes || nodes.length < 2) return suggestions;

    nodes.forEach((sourceNode, i) => {
      nodes.forEach((targetNode, j) => {
        if (i !== j) {
          const exists = edges.some(edge => 
            edge.source === sourceNode.id && edge.target === targetNode.id
          );

          if (!exists) {
            const sourceLifecycle = this.lifecycleDB[sourceNode.data?.lifecycleId] || 
                                   sourceNode.data?.isCustom ? { 
                                     name: sourceNode.data.label,
                                     partners: []
                                   } : null;
            
            const targetLifecycle = this.lifecycleDB[targetNode.data?.lifecycleId] || 
                                   targetNode.data?.isCustom ? { 
                                     name: targetNode.data.label,
                                     partners: []
                                   } : null;

            let score = 0;
            let reason = '';

            if (sourceLifecycle && targetLifecycle) {
              const isNaturalPartner = sourceLifecycle.partners?.includes(targetLifecycle.id) ||
                                      targetLifecycle.partners?.includes(sourceLifecycle.id);
              
              if (isNaturalPartner) {
                score = 0.9;
                reason = 'Natural lifecycle partnership';
              } else {
                const sourceCategory = sourceNode.data?.category;
                const targetCategory = targetNode.data?.category;
                
                if (sourceCategory === targetCategory) {
                  score = 0.7;
                  reason = 'Same category synergy';
                } else if (this.areCategoriesCompatible(sourceCategory, targetCategory)) {
                  score = 0.6;
                  reason = 'Category compatibility';
                }
              }
            }

            score += (Math.random() * 0.2) - 0.1;
            score = Math.max(0.1, Math.min(0.95, score));

            if (score > 0.5) {
              suggestions.push({
                source: sourceNode.id,
                target: targetNode.id,
                sourceName: sourceNode.data?.label || 'Unknown',
                targetName: targetNode.data?.label || 'Unknown',
                reason: reason || 'Potential workflow connection',
                confidence: parseFloat(score.toFixed(2)),
                sourceCategory: sourceNode.data?.category,
                targetCategory: targetNode.data?.category
              });
            }
          }
        }
      });
    });

    return suggestions.sort((a, b) => b.confidence - a.confidence).slice(0, 10);
  }

  areCategoriesCompatible(cat1, cat2) {
    const compatibilityMap = {
      'STRATEGIC': ['OPERATIONAL', 'ENABLING'],
      'OPERATIONAL': ['STRATEGIC', 'ENGINEERING'],
      'ENGINEERING': ['OPERATIONAL', 'DIGITAL'],
      'DIGITAL': ['ENGINEERING', 'ENABLING'],
      'ENABLING': ['STRATEGIC', 'DIGITAL']
    };
    
    return compatibilityMap[cat1]?.includes(cat2) || compatibilityMap[cat2]?.includes(cat1);
  }

  generateImplementationSteps(lifecycle) {
    return [
      {
        id: 'assessment',
        title: 'Assess Current State',
        description: `Evaluate current ${lifecycle.name} maturity`,
        duration: '1-2 days',
        resources: ['Business Analyst', 'Domain Expert'],
        dependencies: []
      },
      {
        id: 'planning',
        title: 'Integration Planning',
        description: 'Design integration points with connected systems',
        duration: '3-5 days',
        resources: ['Solution Architect', 'Integration Specialist'],
        dependencies: ['assessment']
      },
      {
        id: 'implementation',
        title: 'Implementation',
        description: 'Deploy and configure lifecycle integration',
        duration: '2-4 weeks',
        resources: ['DevOps Engineer', 'System Administrator'],
        dependencies: ['planning']
      }
    ];
  }

  identifyPrerequisites(lifecycle) {
    return lifecycle.partners?.map(partnerId => ({
      lifecycle: partnerId,
      name: this.lifecycleDB[partnerId]?.name || partnerId,
      priority: 'HIGH',
      estimatedEffort: '1-2 weeks'
    })) || [];
  }
}

// ==================== FLOW COMPONENT ====================
const FlowComponent = React.memo(({ 
  nodes, 
  edges, 
  onNodesChange, 
  onEdgesChange, 
  onConnect, 
  setReactFlowInstance,
  selectedNode,
  setSelectedNode,
  customLifecycles,
  generateAIRecommendations,
  isAnalyzing,
  saveWorkflow,
  toggleFullscreen,
  isFullscreen,
  handleZoomIn,
  handleZoomOut,
  handleFitView,
  reactFlowInstance,
  showNotification,
  applyAutoLayout,
  isAnimatingLayout,
  currentLayout,
  layoutEngine,
  cycleThroughLayouts,
  layoutHistory,
  undoLastLayout,
  autoConnectWithLayout
}) => {
  
  const edgeTypes = useMemo(() => ({
    quantumEdge: (props) => {
      const { sourceX, sourceY, targetX, targetY, data = {} } = props;
      const strength = data.strength || 0.5;
      const edgeColor = data.color || QUANTUM_COLORS.ENTANGLED;
      const isAnimated = data.animated || strength > 0.7;
      
      const path = `M ${sourceX},${sourceY} C ${sourceX + 100},${sourceY} ${targetX - 100},${targetY} ${targetX},${targetY}`;
      
      return (
        <>
          <defs>
            <marker
              id={`arrow-${props.id}`}
              markerWidth="12"
              markerHeight="12"
              refX="10"
              refY="6"
              orient="auto"
              markerUnits="strokeWidth"
            >
              <polygon
                points="0,0 0,12 12,6"
                fill={edgeColor}
                stroke={edgeColor}
                strokeWidth="1"
              />
            </marker>
          </defs>
          <path
            id={props.id}
            d={path}
            fill="none"
            stroke={edgeColor}
            strokeWidth={Math.max(2, strength * 4)}
            strokeDasharray={strength > 0.8 ? "0" : "5,5"}
            markerEnd={`url(#arrow-${props.id})`}
            style={{
              filter: `drop-shadow(0 0 8px ${edgeColor}80)`,
              animation: isAnimated ? 'dashdraw 1s linear infinite' : 'none'
            }}
          />
        </>
      );
    }
  }), []);

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      onConnect={onConnect}
      onInit={setReactFlowInstance}
      onNodeClick={(e, node) => setSelectedNode(node)}
      onEdgeClick={(e, edge) => {
        console.log('Edge clicked:', edge);
        showNotification(`Connection strength: ${(edge.data?.strength * 100).toFixed(0)}%`, 'info');
      }}
      nodeTypes={{
        lifecycleNode: LifecycleNode
      }}
      edgeTypes={edgeTypes}
      connectionLineType={ConnectionLineType.SmoothStep}
      connectionLineStyle={{
        stroke: QUANTUM_COLORS.SUPERPOSITION,
        strokeWidth: 3,
        strokeDasharray: "5,5"
      }}
      defaultEdgeOptions={{
        type: 'quantumEdge',
        animated: true,
        style: {
          stroke: QUANTUM_COLORS.ENTANGLED,
          strokeWidth: 2,
        },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          width: 20,
          height: 20,
          color: QUANTUM_COLORS.ENTANGLED
        }
      }}
      fitView
      fitViewOptions={{ padding: 0.2 }}
      attributionPosition="bottom-left"
      style={{ background: 'transparent' }}
      minZoom={0.1}
      maxZoom={2}
      nodesDraggable={true}
      nodesConnectable={true}
      elementsSelectable={true}
      selectNodesOnDrag={false}
    >
      <Background 
        variant="dots" 
        gap={30} 
        size={1.5} 
        color="rgba(255, 255, 255, 0.05)"
        style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.05) 1px, transparent 0)',
          backgroundSize: '30px 30px'
        }}
      />
      
      <Controls 
        style={{ 
          background: 'rgba(15, 23, 42, 0.9)',
          border: '1px solid rgba(255, 255, 255, 0.15)',
          borderRadius: '12px',
          padding: '8px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
        }}
        showInteractive={false}
      >
        <ControlButton onClick={handleZoomIn} title="Zoom In">
          <ZoomIn sx={{ color: 'white' }} />
        </ControlButton>
        <ControlButton onClick={handleZoomOut} title="Zoom Out">
          <ZoomOut sx={{ color: 'white' }} />
        </ControlButton>
        <ControlButton onClick={handleFitView} title="Fit View">
          <FitScreen sx={{ color: 'white' }} />
        </ControlButton>
        <ControlButton onClick={toggleFullscreen} title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}>
          {isFullscreen ? <FullscreenExit sx={{ color: 'white' }} /> : <Fullscreen sx={{ color: 'white' }} />}
        </ControlButton>
      </Controls>
      
      <MiniMap 
        style={{ 
          background: 'rgba(15, 23, 42, 0.9)',
          border: '1px solid rgba(255, 255, 255, 0.15)',
          borderRadius: '8px',
          backdropFilter: 'blur(10px)'
        }}
        nodeStrokeColor={(node) => {
          const lifecycle = LIFECYCLE_DATABASE[node.data?.lifecycleId] || 
                          customLifecycles.find(l => l.id === node.data?.lifecycleId);
          return lifecycle ? 
            LIFECYCLE_CATEGORY_COLORS[lifecycle.category]?.split(' ')[2] || '#6366F1' : 
            QUANTUM_COLORS[node.data?.quantumState || QUANTUM_STATES.COHERENT];
        }}
        nodeColor={(node) => {
          const opacity = node.selected ? '0.8' : '0.3';
          const lifecycle = LIFECYCLE_DATABASE[node.data?.lifecycleId] || 
                          customLifecycles.find(l => l.id === node.data?.lifecycleId);
          const color = lifecycle ? 
            LIFECYCLE_CATEGORY_COLORS[lifecycle.category]?.split(' ')[2] || '#6366F1' : 
            QUANTUM_COLORS[node.data?.quantumState || QUANTUM_STATES.COHERENT];
          return color + opacity;
        }}
        maskColor="rgba(15, 23, 42, 0.6)"
        zoomable
        pannable
      />
      
      <Panel position="top-right" style={{ margin: '10px' }}>
        <Box sx={{ 
          display: 'flex', 
          gap: 1, 
          flexWrap: 'wrap',
          background: 'rgba(15, 23, 42, 0.85)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.15)',
          borderRadius: '12px',
          padding: '12px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)'
        }}>
          <Tooltip title="Auto Layout & Reconnect">
            <Button
              variant="contained"
              startIcon={isAnimatingLayout ? <CircularProgress size={16} /> : <AutoAwesome />}
              onClick={() => applyAutoLayout()}
              disabled={isAnimatingLayout || nodes.length === 0}
              size="small"
              sx={{
                background: `linear-gradient(135deg, ${QUANTUM_COLORS.SUPERPOSITION} 0%, ${QUANTUM_COLORS.INTERFERENCE} 100%)`,
                color: 'white',
                '&:hover': {
                  background: `linear-gradient(135deg, ${QUANTUM_COLORS.INTERFERENCE} 0%, ${QUANTUM_COLORS.DECOHERENT} 100%)`,
                  transform: 'translateY(-2px)',
                  boxShadow: `0 8px 20px ${QUANTUM_COLORS.INTERFERENCE}80`
                },
                '&.Mui-disabled': {
                  background: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              {isAnimatingLayout ? 'Transforming...' : 'Auto Layout'}
            </Button>
          </Tooltip>

          <Tooltip title="Zoom In">
            <IconButton 
              onClick={handleZoomIn} 
              sx={{ 
                background: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                '&:hover': { background: QUANTUM_COLORS.COHERENT }
              }}
            >
              <ZoomIn />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Zoom Out">
            <IconButton 
              onClick={handleZoomOut}
              sx={{ 
                background: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                '&:hover': { background: QUANTUM_COLORS.COHERENT }
              }}
            >
              <ZoomOut />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Fit View">
            <IconButton 
              onClick={handleFitView}
              sx={{ 
                background: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                '&:hover': { background: QUANTUM_COLORS.COHERENT }
              }}
            >
              <FitScreen />
            </IconButton>
          </Tooltip>
          
          <Tooltip title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}>
            <IconButton 
              onClick={toggleFullscreen}
              sx={{ 
                background: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                '&:hover': { background: QUANTUM_COLORS.SUPERPOSITION }
              }}
            >
              {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
            </IconButton>
          </Tooltip>
          
          <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255, 255, 255, 0.2)' }} />
          
          <Tooltip title="AI Analysis">
            <Button
              variant="contained"
              startIcon={isAnalyzing ? <CircularProgress size={16} /> : <AutoAwesome />}
              onClick={generateAIRecommendations}
              disabled={isAnalyzing}
              size="small"
              sx={{
                background: `linear-gradient(135deg, ${QUANTUM_COLORS.COHERENT} 0%, ${QUANTUM_COLORS.SUPERPOSITION} 100%)`,
                color: 'white',
                '&:hover': {
                  background: `linear-gradient(135deg, ${QUANTUM_COLORS.SUPERPOSITION} 0%, ${QUANTUM_COLORS.ENTANGLED} 100%)`,
                  transform: 'translateY(-2px)',
                  boxShadow: `0 8px 20px ${QUANTUM_COLORS.SUPERPOSITION}80`
                },
                '&.Mui-disabled': {
                  background: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              {isAnalyzing ? 'Analyzing...' : 'AI Analysis'}
            </Button>
          </Tooltip>
          
          <Tooltip title="Save Workflow">
            <Button
              variant="outlined"
              startIcon={<Save />}
              onClick={saveWorkflow}
              size="small"
              sx={{
                borderColor: QUANTUM_COLORS.COHERENT,
                color: QUANTUM_COLORS.COHERENT,
                '&:hover': {
                  borderColor: QUANTUM_COLORS.ENTANGLED,
                  background: 'rgba(16, 185, 129, 0.1)',
                  transform: 'translateY(-2px)'
                }
              }}
            >
              Save
            </Button>
          </Tooltip>
        </Box>
      </Panel>
      
      <Panel position="bottom-left" style={{ margin: '10px' }}>
        {edges.length > 0 && (
          <Card sx={{ 
            background: 'rgba(15, 23, 42, 0.9)',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            borderRadius: '12px',
            backdropFilter: 'blur(10px)',
            maxWidth: '300px'
          }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', display: 'block', mb: 1 }}>
                Active Connections: {edges.length}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip 
                  label="Strong" 
                  size="small" 
                  sx={{ 
                    background: QUANTUM_COLORS.COHERENT,
                    color: 'white',
                    fontSize: '0.7rem'
                  }}
                />
                <Chip 
                  label="Medium" 
                  size="small" 
                  sx={{ 
                    background: QUANTUM_COLORS.INTERFERENCE,
                    color: 'white',
                    fontSize: '0.7rem'
                  }}
                />
                <Chip 
                  label="Weak" 
                  size="small" 
                  sx={{ 
                    background: QUANTUM_COLORS.DECOHERENT,
                    color: 'white',
                    fontSize: '0.7rem'
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        )}
      </Panel>
    </ReactFlow>
  );
});

FlowComponent.displayName = 'FlowComponent';

// ==================== MAIN COMPONENT ====================
const QuantumWorkflowOrchestrator = () => {
  // Core State
  const [quantumState, setQuantumState] = useState(QUANTUM_STATES.COHERENT);
  const [workflows, setWorkflows] = useState([]);
  const [activeWorkflow, setActiveWorkflow] = useState(null);
  const [quantumCoherence, setQuantumCoherence] = useState(0.85);
  const [quantumEntanglement, setQuantumEntanglement] = useState(0);
  const [quantumSuperposition, setQuantumSuperposition] = useState([]);
  
  // AI Engine
  const [aiEngine] = useState(() => new QuantumAIEngine());
  const [aiRecommendations, setAiRecommendations] = useState([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  
  // React Flow
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  
  // UI State
  const [viewMode, setViewMode] = useState('dashboard');
  const [selectedNode, setSelectedNode] = useState(null);
  const [activePanel, setActivePanel] = useState('analytics');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [simulationSpeed, setSimulationSpeed] = useState(1);
  const [quantumVisualization, setQuantumVisualization] = useState(true);
  
  // Custom Workflows
  const [customLifecycles, setCustomLifecycles] = useState([]);
  const [showAddCustomModal, setShowAddCustomModal] = useState(false);
  const [newCustomLifecycle, setNewCustomLifecycle] = useState({
    name: '',
    category: 'CUSTOM',
    impactWeight: 0.5,
    phases: [],
    connectedThreads: []
  });
  
  // Drag & Drop
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [dragItem, setDragItem] = useState(null);
  
  // Notifications
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  // Tutorial
  const [showHelp, setShowHelp] = useState(false);
  const [firstVisit, setFirstVisit] = useState(true);
  
  // Data State
  const [lifecycleMetrics, setLifecycleMetrics] = useState({});
  const [threadConnections, setThreadConnections] = useState([]);
  const [valueFlow, setValueFlow] = useState([]);
  
  // Layout Engine State
  const [layoutEngine] = useState(() => new QuantumLayoutEngine());
  const [currentLayout, setCurrentLayout] = useState(layoutEngine.layouts.CIRCULAR);
  const [isAnimatingLayout, setIsAnimatingLayout] = useState(false);
  const [layoutHistory, setLayoutHistory] = useState([]);
  
  // Sequence Analysis
  const [lifecycleSequence, setLifecycleSequence] = useState(null);
  const [connectionSuggestions, setConnectionSuggestions] = useState([]);
  const [sequenceMetrics, setSequenceMetrics] = useState({
    connectivity: 0,
    linearity: false,
    startNodes: 0,
    endNodes: 0
  });

  // Refs
  const containerRef = useRef(null);
  const quantumBackgroundRef = useRef(null);
  const lastAnalysisRef = useRef(null);

  // ==================== LAYOUT FUNCTIONS ====================
  const showNotification = useCallback((message, severity = 'success') => {
    setNotification({
      open: true,
      message,
      severity
    });
  }, []);


  // ==================== NOTIFICATION HANDLER ====================


  const handleCloseNotification = useCallback(() => {
    setNotification(prev => ({ ...prev, open: false }));
  }, []);

  // ==================== WORKFLOW OPERATIONS ====================
  const createNewWorkflow = useCallback((name, description) => {
    const newWorkflow = {
      id: `workflow_${Date.now()}`,
      name: name || 'Untitled Workflow',
      description: description || 'New workflow created',
      createdAt: new Date().toISOString(),
      quantumCoherence: quantumCoherence || 0,
      nodes: nodes || [],
      edges: edges || [],
      metrics: {},
      aiInsights: [],
      customLifecycles: customLifecycles || []
    };
    
    setWorkflows(prev => [...prev, newWorkflow]);
    setActiveWorkflow(newWorkflow);
    
    return newWorkflow;
  }, [quantumCoherence, nodes, edges, customLifecycles]);

  const saveWorkflow = useCallback(() => {
    try {
      if (!activeWorkflow) {
        const newWorkflow = createNewWorkflow('Untitled Workflow', 'New workflow created');
        setActiveWorkflow(newWorkflow);
      }
      
      const workflowToSave = activeWorkflow || createNewWorkflow('Untitled Workflow', 'New workflow created');
      
      const updatedWorkflow = {
        ...workflowToSave,
        id: workflowToSave.id || `workflow_${Date.now()}`,
        name: workflowToSave.name || 'Untitled Workflow',
        nodes: nodes || [],
        edges: edges || [],
        quantumCoherence: quantumCoherence || 0,
        lastSaved: new Date().toISOString(),
        customLifecycles: customLifecycles || []
      };
      
      setWorkflows(prev => {
        const existingIndex = prev.findIndex(w => w.id === updatedWorkflow.id);
        if (existingIndex >= 0) {
          const updated = [...prev];
          updated[existingIndex] = updatedWorkflow;
          return updated;
        } else {
          return [...prev, updatedWorkflow];
        }
      });
      
      setActiveWorkflow(updatedWorkflow);
      showNotification('Workflow saved successfully!', 'success');
      
      console.log('Workflow saved:', {
        id: updatedWorkflow.id,
        nodes: updatedWorkflow.nodes.length,
        edges: updatedWorkflow.edges.length,
        coherence: updatedWorkflow.quantumCoherence
      });
      
    } catch (error) {
      console.error('Failed to save workflow:', error);
      showNotification('Failed to save workflow: ' + error.message, 'error');
    }
  }, [activeWorkflow, nodes, edges, quantumCoherence, customLifecycles, createNewWorkflow, showNotification]);

  const optimizeWorkflow = async () => {
    setIsAnalyzing(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setQuantumCoherence(prev => Math.min(0.95, prev + 0.1));
      setQuantumEntanglement(prev => prev + Math.floor(Math.random() * 3));
      
      calculateQuantumMetrics();
      showNotification('Workflow optimized successfully!', 'success');
    } catch (error) {
      console.error('Optimization failed:', error);
      showNotification('Optimization failed', 'error');
    } finally {
      setIsAnalyzing(false);
    }
  };

  // ==================== SEQUENCE ANALYSIS ====================


//MANDAR
// ==================== LAYOUT FUNCTIONS ====================
const applyAutoLayout = useCallback((layoutType = null) => {
  if (nodes.length === 0) {
    showNotification('Add nodes before applying layout', 'warning');
    return;
  }

  setIsAnimatingLayout(true);
  
  const canvasWidth = window.innerWidth - 320 - 400;
  const canvasHeight = window.innerHeight - 112;
  const selectedLayout = layoutType || currentLayout;
  
  // Save current layout to history
  setLayoutHistory(prev => [...prev.slice(-4), {
    nodes: nodes.map(n => ({ id: n.id, position: n.position })),
    layout: currentLayout,
    timestamp: new Date().toISOString()
  }]);
  
  // Animate nodes to new positions
  const newNodes = layoutEngine.applyLayout(
    nodes, 
    edges, 
    selectedLayout, 
    canvasWidth, 
    canvasHeight
  );
  
  // Create visual animation
  newNodes.forEach((node, index) => {
    const nodeElement = document.querySelector(`[data-id="${node.id}"]`);
    if (nodeElement) {
      nodeElement.style.transition = 'all 1.2s cubic-bezier(0.34, 1.56, 0.64, 1)';
      nodeElement.style.transform = 'scale(1.2) rotate(5deg)';
      nodeElement.style.filter = 'brightness(1.3) drop-shadow(0 0 20px rgba(255,255,255,0.5))';
      
      setTimeout(() => {
        nodeElement.style.transform = 'scale(1) rotate(0deg)';
        nodeElement.style.filter = 'brightness(1) drop-shadow(0 0 0px rgba(255,255,255,0))';
      }, 600 + index * 50);
    }
  });
  
  // Update nodes with delay for cascade effect
  setTimeout(() => {
    setNodes(newNodes);
    setCurrentLayout(selectedLayout);
    
    // Generate new connections based on layout
    regenerateConnections(newNodes);
    
    // Add special effects based on layout type
    applyLayoutSpecialEffects(selectedLayout);
    
    showNotification(
      `Applied ${selectedLayout.replace('_', ' ')} layout with ${edges.length} connections`, 
      'success'
    );
    
    setIsAnimatingLayout(false);
  }, 800);
}, [nodes, edges, currentLayout, layoutEngine, showNotification]);

const regenerateConnections = useCallback((positionedNodes) => {
  if (positionedNodes.length < 2) return;
  
  const newEdges = [];
  const aiSuggestions = aiEngine.generateConnectionSuggestions(positionedNodes, edges);
  
  // Clear existing edges
  setEdges([]);
  
  // Add new connections with animation delay
  aiSuggestions.slice(0, 8).forEach((suggestion, index) => {
    setTimeout(() => {
      const sourceNode = positionedNodes.find(n => n.id === suggestion.source);
      const targetNode = positionedNodes.find(n => n.id === suggestion.target);
      
      if (sourceNode && targetNode) {
        const connectionStrength = suggestion.confidence;
        let edgeColor = QUANTUM_COLORS.ENTANGLED;
        if (connectionStrength > 0.8) edgeColor = QUANTUM_COLORS.COHERENT;
        else if (connectionStrength > 0.6) edgeColor = QUANTUM_COLORS.SUPERPOSITION;
        else if (connectionStrength > 0.4) edgeColor = QUANTUM_COLORS.INTERFERENCE;
        
        const newEdge = {
          id: `edge_${suggestion.source}_${suggestion.target}_${Date.now()}_${index}`,
          source: suggestion.source,
          target: suggestion.target,
          type: 'quantumEdge',
          animated: connectionStrength > 0.7,
          style: {
            stroke: edgeColor,
            strokeWidth: Math.max(2, connectionStrength * 4),
          },
          label: `${(connectionStrength * 100).toFixed(0)}%`,
          labelStyle: { 
            fill: 'white',
            fontWeight: 'bold',
            fontSize: '11px'
          },
          labelBgStyle: { fill: edgeColor },
          labelBgPadding: [6, 8],
          labelBgBorderRadius: 6,
          data: {
            confidence: connectionStrength,
            reason: suggestion.reason,
            type: 'auto-layout',
            color: edgeColor,
            strength: connectionStrength,
            animated: connectionStrength > 0.7
          }
        };
        
        setEdges(prev => [...prev, newEdge]);
        
        // Animate edge appearance
        setTimeout(() => {
          const edgeElement = document.querySelector(`[data-id="${newEdge.id}"]`);
          if (edgeElement) {
            edgeElement.style.strokeDasharray = '1000';
            edgeElement.style.strokeDashoffset = '1000';
            edgeElement.style.animation = 'drawEdge 1.5s ease-out forwards';
          }
        }, 100);
      }
    }, index * 200);
  });
}, [aiEngine, edges, setEdges]);

const applyLayoutSpecialEffects = useCallback((layoutType) => {
  // Add CSS animations based on layout type
  const effects = {
    [layoutEngine.layouts.CIRCULAR]: () => {
      document.querySelectorAll('.react-flow__node').forEach((node, i) => {
        node.style.animation = `circularFloat 3s ease-in-out ${i * 0.1}s infinite`;
      });
    },
    [layoutEngine.layouts.FORCE_DIRECTED]: () => {
      document.querySelectorAll('.react-flow__node').forEach(node => {
        node.style.animation = 'pulseGravity 2s ease-in-out infinite';
      });
    },
    [layoutEngine.layouts.QUANTUM_WAVE]: () => {
      document.querySelectorAll('.react-flow__node').forEach((node, i) => {
        node.style.animation = `quantumWave 4s ease-in-out ${i * 0.2}s infinite`;
      });
      document.querySelectorAll('.react-flow__edge').forEach(edge => {
        edge.style.animation = 'edgeGlow 2s ease-in-out infinite';
      });
    },
    [layoutEngine.layouts.COSMIC]: () => {
      document.querySelectorAll('.react-flow__node').forEach(node => {
        node.style.filter = 'drop-shadow(0 0 15px rgba(255,255,255,0.3))';
      });
    },
    [layoutEngine.layouts.SPIRAL]: () => {
      document.querySelectorAll('.react-flow__node').forEach((node, i) => {
        node.style.animation = `spiralRotate 6s linear ${i * 0.3}s infinite`;
      });
    }
  };
  
  if (effects[layoutType]) {
    effects[layoutType]();
  }
}, [layoutEngine]);

const cycleThroughLayouts = useCallback(() => {
  if (isAnimatingLayout) return;
  
  const layoutTypes = Object.values(layoutEngine.layouts);
  const currentIndex = layoutTypes.indexOf(currentLayout);
  const nextIndex = (currentIndex + 1) % layoutTypes.length;
  const nextLayout = layoutTypes[nextIndex];
  
  applyAutoLayout(nextLayout);
}, [currentLayout, layoutEngine, applyAutoLayout, isAnimatingLayout]);

const undoLastLayout = useCallback(() => {
  if (layoutHistory.length === 0) {
    showNotification('No layout history to undo', 'info');
    return;
  }
  
  const lastLayout = layoutHistory[layoutHistory.length - 1];
  const restoredNodes = nodes.map(node => {
    const savedPosition = lastLayout.nodes.find(n => n.id === node.id);
    return savedPosition ? { ...node, position: savedPosition.position } : node;
  });
  
  setNodes(restoredNodes);
  setCurrentLayout(lastLayout.layout);
  setLayoutHistory(prev => prev.slice(0, -1));
  
  showNotification(`Restored ${lastLayout.layout.replace('_', ' ')} layout`, 'success');
}, [layoutHistory, nodes, setNodes, showNotification]);

// ==================== SEQUENCE ANALYSIS ====================
const analyzeSequence = useCallback(() => {
  if (!aiEngine) return;
  
  try {
    const sequenceAnalysis = aiEngine.analyzeLifecycleSequence(nodes, edges);
    setLifecycleSequence(sequenceAnalysis);
    
    const suggestions = aiEngine.generateConnectionSuggestions(nodes, edges);
    setConnectionSuggestions(suggestions);
    
    setSequenceMetrics({
      connectivity: sequenceAnalysis.connectivityRatio,
      linearity: sequenceAnalysis.hasLinearPath,
      startNodes: sequenceAnalysis.startNodes.length,
      endNodes: sequenceAnalysis.endNodes.length
    });
    
    showNotification(`Sequence analysis complete! Found ${suggestions.length} connection suggestions`, 'success');
  } catch (error) {
    console.error('Sequence analysis failed:', error);
    showNotification('Sequence analysis failed', 'error');
  }
}, [nodes, edges, aiEngine, showNotification]);

const autoConnectSuggestions = useCallback(() => {
  if (connectionSuggestions.length === 0) {
    showNotification('No connection suggestions available', 'warning');
    return;
  }

  const newEdges = [...edges];
  let connectionsMade = 0;
  const maxConnections = Math.min(5, connectionSuggestions.length);

  connectionSuggestions.slice(0, maxConnections).forEach(suggestion => {
    const exists = newEdges.some(edge => 
      edge.source === suggestion.source && edge.target === suggestion.target
    );
    
    if (!exists) {
      const sourceNode = nodes.find(n => n.id === suggestion.source);
      const targetNode = nodes.find(n => n.id === suggestion.target);
      
      if (!sourceNode || !targetNode) return;

      let edgeColor = QUANTUM_COLORS.ENTANGLED;
      if (suggestion.confidence > 0.8) edgeColor = QUANTUM_COLORS.COHERENT;
      else if (suggestion.confidence > 0.6) edgeColor = QUANTUM_COLORS.SUPERPOSITION;
      else if (suggestion.confidence > 0.4) edgeColor = QUANTUM_COLORS.INTERFERENCE;
      else edgeColor = QUANTUM_COLORS.DECOHERENT;

      const edgeId = `edge_${suggestion.source}_${suggestion.target}_${Date.now()}`;
      
      newEdges.push({
        id: edgeId,
        source: suggestion.source,
        target: suggestion.target,
        type: 'quantumEdge',
        animated: suggestion.confidence > 0.7,
        style: {
          stroke: edgeColor,
          strokeWidth: Math.max(2, suggestion.confidence * 4),
        },
        label: `${(suggestion.confidence * 100).toFixed(0)}%`,
        labelStyle: { 
          fill: 'white',
          fontWeight: 'bold',
          fontSize: '11px'
        },
        labelBgStyle: { fill: edgeColor },
        labelBgPadding: [6, 8],
        labelBgBorderRadius: 6,
        data: {
          confidence: suggestion.confidence,
          reason: suggestion.reason,
          type: 'auto-connected',
          color: edgeColor,
          strength: suggestion.confidence,
          animated: suggestion.confidence > 0.7
        }
      });
      
      connectionsMade++;
    }
  });

  setEdges(newEdges);
  
  if (connectionsMade > 0) {
    showNotification(`Auto-connected ${connectionsMade} lifecycles`, 'success');
    setTimeout(() => analyzeSequence(), 500);
  } else {
    showNotification('All suggested connections already exist', 'info');
  }
}, [connectionSuggestions, edges, nodes, analyzeSequence, showNotification, setEdges]);

// ==================== AUTO CONNECT WITH LAYOUT ====================
const autoConnectWithLayout = useCallback(() => {
  if (nodes.length === 0) {
    showNotification('Add nodes before auto-connecting', 'warning');
    return;
  }
  
  setIsAnalyzing(true);
  
  // 1. First apply a random impressive layout
  const layoutTypes = Object.values(layoutEngine.layouts);
  const randomLayout = layoutTypes[Math.floor(Math.random() * layoutTypes.length)];
  
  // 2. Apply layout with animation
  setTimeout(() => {
    applyAutoLayout(randomLayout);
    
    // 3. Then create intelligent connections
    setTimeout(() => {
      // Get fresh connection suggestions after layout
      const aiSuggestions = aiEngine.generateConnectionSuggestions(nodes, edges);
      setConnectionSuggestions(aiSuggestions);
      
      const topSuggestions = aiSuggestions.slice(0, Math.min(8, aiSuggestions.length));
      
      if (topSuggestions.length > 0) {
        // Create connections directly instead of calling autoConnectSuggestions
        const newEdges = [...edges];
        let connectionsMade = 0;
        
        topSuggestions.forEach(suggestion => {
          const exists = newEdges.some(edge => 
            edge.source === suggestion.source && edge.target === suggestion.target
          );
          
          if (!exists) {
            const sourceNode = nodes.find(n => n.id === suggestion.source);
            const targetNode = nodes.find(n => n.id === suggestion.target);
            
            if (!sourceNode || !targetNode) return;

            let edgeColor = QUANTUM_COLORS.ENTANGLED;
            if (suggestion.confidence > 0.8) edgeColor = QUANTUM_COLORS.COHERENT;
            else if (suggestion.confidence > 0.6) edgeColor = QUANTUM_COLORS.SUPERPOSITION;
            else if (suggestion.confidence > 0.4) edgeColor = QUANTUM_COLORS.INTERFERENCE;
            else edgeColor = QUANTUM_COLORS.DECOHERENT;

            const edgeId = `edge_${suggestion.source}_${suggestion.target}_${Date.now()}`;
            
            newEdges.push({
              id: edgeId,
              source: suggestion.source,
              target: suggestion.target,
              type: 'quantumEdge',
              animated: suggestion.confidence > 0.7,
              style: {
                stroke: edgeColor,
                strokeWidth: Math.max(2, suggestion.confidence * 4),
              },
              label: `${(suggestion.confidence * 100).toFixed(0)}%`,
              labelStyle: { 
                fill: 'white',
                fontWeight: 'bold',
                fontSize: '11px'
              },
              labelBgStyle: { fill: edgeColor },
              labelBgPadding: [6, 8],
              labelBgBorderRadius: 6,
              data: {
                confidence: suggestion.confidence,
                reason: suggestion.reason,
                type: 'auto-layout-connected',
                color: edgeColor,
                strength: suggestion.confidence,
                animated: suggestion.confidence > 0.7
              }
            });
            
            connectionsMade++;
          }
        });

        setEdges(newEdges);
        
        if (connectionsMade > 0) {
          showNotification(
            `Applied ${randomLayout.replace('_', ' ')} layout with ${connectionsMade} new connections`, 
            'success'
          );
          setTimeout(() => analyzeSequence(), 500);
        } else {
          showNotification('All suggested connections already exist', 'info');
        }
      } else {
        showNotification('No connection suggestions found after layout', 'info');
      }
      
      setIsAnalyzing(false);
    }, 1500);
  }, 500);
}, [nodes, edges, layoutEngine, aiEngine, applyAutoLayout, showNotification, setIsAnalyzing, setEdges, analyzeSequence, setConnectionSuggestions]);
//MANDAR- End

  const onConnect = useCallback((params) => {
    const sourceNode = nodes.find(n => n.id === params.source);
    const targetNode = nodes.find(n => n.id === params.target);
    
    if (!sourceNode || !targetNode) {
      showNotification('Cannot connect: Node not found', 'error');
      return;
    }

    const sourceLifecycle = LIFECYCLE_DATABASE[sourceNode.data?.lifecycleId] || 
                           customLifecycles.find(l => l.id === sourceNode.data?.lifecycleId);
    const targetLifecycle = LIFECYCLE_DATABASE[targetNode.data?.lifecycleId] || 
                           customLifecycles.find(l => l.id === targetNode.data?.lifecycleId);
    
    let connectionStrength = 0.5;
    let connectionType = 'standard';
    let connectionColor = QUANTUM_COLORS.ENTANGLED;
    
    if (sourceLifecycle?.partners?.includes(targetLifecycle?.id)) {
      connectionStrength = 0.9;
      connectionType = 'partnership';
      connectionColor = QUANTUM_COLORS.COHERENT;
    } else if (sourceNode.data?.category === targetNode.data?.category) {
      connectionStrength = 0.7;
      connectionType = 'category';
      connectionColor = QUANTUM_COLORS.SUPERPOSITION;
    }

    const newEdge = {
      ...params,
      id: `edge_${params.source}_${params.target}_${Date.now()}`,
      type: 'quantumEdge',
      animated: true,
      style: { 
        stroke: connectionColor,
        strokeWidth: Math.max(2, connectionStrength * 4),
      },
      label: `${(connectionStrength * 100).toFixed(0)}%`,
      labelStyle: { 
        fill: 'white',
        fontWeight: 'bold',
        fontSize: '11px'
      },
      labelBgStyle: { fill: connectionColor },
      labelBgPadding: [6, 8],
      labelBgBorderRadius: 6,
      data: {
        strength: connectionStrength,
        type: connectionType,
        color: connectionColor,
        strength: connectionStrength,
        animated: connectionStrength > 0.7,
        createdAt: new Date().toISOString(),
        sourceCategory: sourceNode.data?.category,
        targetCategory: targetNode.data?.category
      }
    };
    
    setEdges((eds) => addEdge(newEdge, eds));
    showNotification(`Connected ${sourceNode.data?.label} → ${targetNode.data?.label}`, 'success');
    
    setTimeout(() => analyzeSequence(), 300);
  }, [nodes, customLifecycles, analyzeSequence, showNotification]);

  const autoConnectLifecycles = () => {
    if (connectionSuggestions.length === 0) return;
    
    const newEdges = [...edges];
    let connectionsMade = 0;
    
    connectionSuggestions.slice(0, 3).forEach(suggestion => {
      const connectionExists = newEdges.some(edge => 
        edge.source === suggestion.source && edge.target === suggestion.target
      );
      
      if (!connectionExists) {
        newEdges.push({
          id: `edge_${suggestion.source}_${suggestion.target}_${Date.now()}`,
          source: suggestion.source,
          target: suggestion.target,
          type: 'smoothstep',
          animated: true,
          style: { stroke: QUANTUM_COLORS.ENTANGLED, strokeWidth: 2 },
          label: `Partner: ${suggestion.confidence * 100}%`,
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: QUANTUM_COLORS.ENTANGLED
          }
        });
        connectionsMade++;
      }
    });
    
    setEdges(newEdges);
    showNotification(`Created ${connectionsMade} new connections`, 'success');
  };

  // ==================== ZOOM & VIEW CONTROLS ====================
  const handleZoomIn = () => {
    if (reactFlowInstance) {
      reactFlowInstance.zoomIn({ duration: 300 });
    }
  };

  const handleZoomOut = () => {
    if (reactFlowInstance) {
      reactFlowInstance.zoomOut({ duration: 300 });
    }
  };

  const handleFitView = () => {
    if (reactFlowInstance) {
      reactFlowInstance.fitView({ duration: 300, padding: 0.1 });
    }
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
    setIsFullscreen(!isFullscreen);
  };

  // ==================== DRAG & DROP FUNCTIONS ====================
  const handleDragStart = (e, lifecycle) => {
    e.dataTransfer.setData('application/json', JSON.stringify({
      type: 'lifecycle',
      data: lifecycle
    }));
    e.dataTransfer.effectAllowed = 'move';
    setDragItem(lifecycle);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDraggingOver(true);
  };

  const handleDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDraggingOver(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDraggingOver(false);
    
    try {
      const data = JSON.parse(e.dataTransfer.getData('application/json'));
      if (data.type === 'lifecycle') {
        addLifecycleNode(data.data);
      }
    } catch (error) {
      console.error('Drop error:', error);
    }
  };

  // ==================== NODE MANAGEMENT ====================
  const initializeSampleNodes = () => {
    const sampleNodes = Object.values(LIFECYCLE_DATABASE).slice(0, 3).map((lifecycle, index) => ({
      id: `node_${lifecycle.id}_${Date.now()}`,
      type: 'lifecycleNode',
      position: { x: 300 + index * 350, y: 200 },
      data: {
        label: lifecycle.name,
        category: lifecycle.category,
        quantumState: Object.values(QUANTUM_STATES)[index],
        impactWeight: lifecycle.impactWeight || 0.5,
        phases: lifecycle.phases || ['Phase 1', 'Phase 2'],
        connectedThreads: lifecycle.connectedThreads || [],
        lifecycleId: lifecycle.id,
        isCustom: false
      }
    }));
    
    setNodes(sampleNodes);
  };

  const addLifecycleNode = (lifecycle) => {
    const canvasWidth = window.innerWidth - 320 - 400;
    const canvasHeight = window.innerHeight - 112;
    
    const newNode = {
      id: `node_${Date.now()}_${lifecycle.id || lifecycle.name.replace(/\s+/g, '_')}`,
      type: 'lifecycleNode',
      position: { 
        x: Math.random() * (canvasWidth - 300) + 50,
        y: Math.random() * (canvasHeight - 200) + 50 
      },
      data: {
        label: lifecycle.name,
        category: lifecycle.category,
        quantumState: QUANTUM_STATES.COHERENT,
        impactWeight: lifecycle.impactWeight || 0.5,
        phases: lifecycle.phases || [],
        connectedThreads: lifecycle.connectedThreads || [],
        lifecycleId: lifecycle.id || `custom_${Date.now()}`,
        isCustom: lifecycle.isCustom || false
      }
    };
    
    setNodes(prev => [...prev, newNode]);
    showNotification(`Added "${lifecycle.name}" to canvas`, 'success');
  };

  const addCustomLifecycle = () => {
    const newLifecycle = {
      ...newCustomLifecycle,
      id: `custom_${Date.now()}`,
      name: newCustomLifecycle.name || `Custom Workflow ${customLifecycles.length + 1}`,
      isCustom: true,
      priority: 'MEDIUM'
    };
    
    setCustomLifecycles(prev => [...prev, newLifecycle]);
    setNewCustomLifecycle({
      name: '',
      category: 'CUSTOM',
      impactWeight: 0.5,
      phases: [],
      connectedThreads: []
    });
    setShowAddCustomModal(false);
    showNotification('Custom workflow created!', 'success');
  };

  // ==================== INITIALIZATION FUNCTIONS ====================
  const initializeQuantumOrchestrator = async () => {
    try {
      calculateQuantumMetrics();
      await generateAIRecommendations();

      setTimeout(() => {
        analyzeSequence();
      }, 1000);
   
      setAiRecommendations([]);
      if (nodes.length > 0) {
        await generateAIRecommendations();
      }
      initializeSampleNodes();
    } catch (error) {
      console.error('Failed to initialize:', error);
      setAiRecommendations([]);
    }
  };

  const initializeQuantumBackground = () => {
    // Background particles initialization
  };

  // ==================== QUANTUM CALCULATIONS ====================
  const calculateQuantumMetrics = useCallback(() => {
    let coherence = 0;
    let entanglementCount = 0;
    const superpositions = [];
    
    nodes.forEach(node => {
      const lifecycle = LIFECYCLE_DATABASE[node.data?.lifecycleId] || 
                       customLifecycles.find(l => l.id === node.data?.lifecycleId);
      if (lifecycle) {
        let nodeCoherence = 1;
        lifecycle.partners?.forEach(partnerId => {
          const partnerNode = nodes.find(n => n.data?.lifecycleId === partnerId);
          if (partnerNode) {
            nodeCoherence *= 0.9;
            entanglementCount++;
          }
        });
        
        coherence += nodeCoherence * (lifecycle.impactWeight || 0);
        
        if (lifecycle.priority === 'CRITICAL+' || lifecycle.priority === 'CRITICAL') {
          superpositions.push({
            lifecycle: lifecycle.id,
            state: QUANTUM_STATES.SUPERPOSITION,
            probability: lifecycle.impactWeight
          });
        }
      }
    });
    
    const normalizedCoherence = nodes.length > 0 ? coherence / nodes.length : 0;
    setQuantumCoherence(normalizedCoherence);
    setQuantumEntanglement(entanglementCount);
    setQuantumSuperposition(superpositions);
    
    if (normalizedCoherence > 0.8) {
      setQuantumState(QUANTUM_STATES.COHERENT);
    } else if (normalizedCoherence > 0.6) {
      setQuantumState(QUANTUM_STATES.ENTANGLED);
    } else if (normalizedCoherence > 0.4) {
      setQuantumState(QUANTUM_STATES.INTERFERENCE);
    } else {
      setQuantumState(QUANTUM_STATES.DECOHERENT);
    }
  }, [nodes, customLifecycles]);

  // ==================== AI FUNCTIONS ====================
  const generateAIRecommendations = async () => {
    setIsAnalyzing(true);
    try {
      const workflowState = {
        nodes: nodes.reduce((acc, node) => {
          acc[node.id] = {
            quantumCoherence: Math.random() * 0.5 + 0.5,
            alignment: Math.random() * 0.8 + 0.2
          };
          return acc;
        }, {}),
        threads: {},
        metrics: lifecycleMetrics || {},
        customLifecycles: customLifecycles || []
      };
      
      const constraints = {
        budget: 1000000,
        timeline: 12,
        teamSize: 50,
        riskTolerance: 0.7
      };
      
      const recommendations = await aiEngine.predictOptimalPath(workflowState, constraints);
      setAiRecommendations(recommendations || []);
      lastAnalysisRef.current = new Date();
      showNotification('AI analysis complete!', 'success');
    } catch (error) {
      console.error('AI analysis failed:', error);
      setAiRecommendations([]);
      showNotification('AI analysis failed', 'error');
    } finally {
      setIsAnalyzing(false);
    }
  };

  // ==================== HELPER FUNCTIONS ====================
  const getLifecycleIcon = (category) => {
    const icons = {
      EXISTENTIAL: <Psychology />,
      FOUNDATIONAL: <Architecture />,
      STRATEGIC: <TrendingUp />,
      OPERATIONAL: <Settings />,
      ENGINEERING: <Engineering />,
      DIGITAL: <Cloud />,
      COMPLIANCE: <Shield />,
      ENABLING: <Bolt />,
      EVOLUTIONARY: <AutoAwesome />,
      KNOWLEDGE: <Book />,
      CUSTOM: <Add />
    };
    return icons[category] || <AccountTree />;
  };

  const highlightConnections = (suggestions) => {
    suggestions.slice(0, 3).forEach((suggestion, index) => {
      const sourceNode = document.querySelector(`[data-id="${suggestion.source}"]`);
      const targetNode = document.querySelector(`[data-id="${suggestion.target}"]`);
      
      if (sourceNode && targetNode) {
        // Add pulsing animation to source node
        sourceNode.style.animation = 'pulseHighlight 1s ease-in-out';
        targetNode.style.animation = 'pulseHighlight 1s ease-in-out 0.2s';
        
        // Remove animation after completion
        setTimeout(() => {
          sourceNode.style.animation = '';
          targetNode.style.animation = '';
        }, 1200);
      }
    });
  };

  const calculateROI = () => {
    return nodes.length * 0.8 + 2.5;
  };

  const calculateTimeReduction = () => {
    return nodes.length * 3 + 15;
  };

  const calculateValueFlow = () => {
    return nodes.reduce((total, node) => {
      return total + (node.data?.impactWeight || 0) * 150000;
    }, 0);
  };

  const detectValueLeakages = () => {
    return [
      { source: 'Unoptimized Handoffs', amount: '$125,000' },
      { source: 'Data Silos', amount: '$89,000' },
      { source: 'Manual Processes', amount: '$67,000' }
    ];
  };

  const formatTimeAgo = (date) => {
    if (!date) return 'Never';
    const seconds = Math.floor((new Date() - date) / 1000);
    if (seconds < 60) return 'Just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  // ==================== USE EFFECT HOOKS ====================
  useEffect(() => {
    initializeQuantumOrchestrator();
    initializeQuantumBackground();
    
    const hasVisited = localStorage.getItem('quantum-orchestrator-visited');
    if (!hasVisited) {
      setTimeout(() => {
        setShowHelp(true);
        localStorage.setItem('quantum-orchestrator-visited', 'true');
      }, 1000);
      setFirstVisit(true);
    }
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        saveWorkflow();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'o') {
        e.preventDefault();
        optimizeWorkflow();
      }
      if (e.key === 'Delete' && selectedNode && selectedNode.id) {
        setNodes(prev => prev.filter(node => node.id !== selectedNode.id));
        setSelectedNode(null);
        showNotification('Node deleted', 'info');
      }
      if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedNode, isFullscreen, saveWorkflow, optimizeWorkflow, setNodes, showNotification]);

  // ==================== RENDER FUNCTIONS ====================
  const renderQuantumVisualization = () => {
    if (!quantumVisualization) return null;
    
    return (
      <Box sx={{ 
        position: 'absolute', 
        top: 0, 
        left: 0, 
        right: 0, 
        bottom: 0,
        pointerEvents: 'none',
        zIndex: 0 
      }}>
        <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}>
          {Array.from({ length: 15 }).map((_, i) => (
            <QuantumParticle
              key={i}
              color={Object.values(QUANTUM_COLORS)[i % Object.values(QUANTUM_COLORS).length]}
              size={Math.random() * 50 + 20}
              animate={{
                x: [Math.random() * 100, Math.random() * 100],
                y: [Math.random() * 100, Math.random() * 100],
                scale: [1, 1.2, 1]
              }}
              transition={{
                duration: Math.random() * 3 + 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`
              }}
            />
          ))}
        </Box>
      </Box>
    );
  };

  const renderQuantumDashboard = () => (
    <QuantumDashboard elevation={0}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight="700" sx={{ color: 'white' }}>
          Quantum Dashboard
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh Metrics">
            <IconButton onClick={calculateQuantumMetrics} sx={{ color: 'white' }}>
              <Refresh />
            </IconButton>
          </Tooltip>
          <Tooltip title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}>
            <IconButton onClick={toggleFullscreen} sx={{ color: 'white' }}>
              {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'rgba(255, 255, 255, 0.05)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Box sx={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '12px',
                  background: QUANTUM_COLORS[quantumState],
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 2
                }}>
                  <AutoAwesome sx={{ fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography variant="h4" fontWeight="800" sx={{ color: 'white' }}>
                    {(quantumCoherence * 100).toFixed(1)}%
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Quantum Coherence
                  </Typography>
                </Box>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={quantumCoherence * 100} 
                sx={{ 
                  height: '6px', 
                  borderRadius: '3px',
                  mt: 1,
                  '& .MuiLinearProgress-bar': {
                    background: QUANTUM_COLORS[quantumState]
                  }
                }}
              />
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'rgba(255, 255, 255, 0.05)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Box sx={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '12px',
                  background: QUANTUM_COLORS.ENTANGLED,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 2
                }}>
                  <Link sx={{ fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography variant="h4" fontWeight="800" sx={{ color: 'white' }}>
                    {quantumEntanglement}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Entanglements
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2" sx={{ mt: 1, color: 'rgba(255, 255, 255, 0.8)' }}>
                {edges.length} connections active
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      <Card sx={{ mb: 3, background: 'rgba(255, 255, 255, 0.05)' }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" fontWeight="700" sx={{ color: 'white' }}>
              AI Recommendations
            </Typography>
            <Button 
              size="small" 
              onClick={generateAIRecommendations}
              disabled={isAnalyzing}
              startIcon={isAnalyzing ? <CircularProgress size={16} /> : <AutoAwesome />}
              sx={{
                background: QUANTUM_COLORS.COHERENT,
                color: 'white',
                '&:hover': { background: QUANTUM_COLORS.ENTANGLED }
              }}
            >
              {isAnalyzing ? 'Analyzing...' : 'Re-analyze'}
            </Button>
          </Box>
          
          <List dense>
            {aiRecommendations && aiRecommendations.length > 0 ? (
              aiRecommendations.slice(0, 3).map((rec, index) => (
                <ListItem
                  key={index}
                  sx={{
                    borderRadius: '8px',
                    mb: 1,
                    background: 'rgba(255, 255, 255, 0.05)'
                  }}
                >
                  <ListItemIcon>
                    <AutoAwesome sx={{ color: QUANTUM_COLORS.COHERENT }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" fontWeight="600" sx={{ color: 'white' }}>
                          {rec.name}
                        </Typography>
                        <Chip 
                          label={`${(rec.confidence * 100).toFixed(0)}%`}
                          size="small"
                          sx={{ 
                            background: rec.confidence > 0.7 ? 
                              QUANTUM_COLORS.COHERENT : 
                              QUANTUM_COLORS.INTERFERENCE,
                            color: 'white'
                          }}
                        />
                      </Box>
                    }
                    secondary={
                      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', display: 'block', mt: 0.5 }}>
                        {rec.reason}
                      </Typography>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Button 
                      size="small" 
                      variant="outlined"
                      sx={{ borderColor: QUANTUM_COLORS.COHERENT, color: QUANTUM_COLORS.COHERENT }}
                    >
                      Apply
                    </Button>
                  </ListItemSecondaryAction>
                </ListItem>
              ))
            ) : (
              <ListItem>
                <ListItemText 
                  primary="No recommendations yet"
                  secondary="Run AI analysis to get recommendations"
                  sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                />
              </ListItem>
            )}
          </List>
          
          {aiRecommendations && aiRecommendations.length > 3 && (
            <Button 
              fullWidth 
              size="small" 
              endIcon={<ChevronRight />}
              sx={{ color: QUANTUM_COLORS.COHERENT }}
            >
              View all {aiRecommendations.length} recommendations
            </Button>
          )}
        </CardContent>
      </Card>
    </QuantumDashboard>
  );

  // ==================== MAIN RENDER ====================
  return (
    <>
      {!isFullscreen && (
        <AppBar position="fixed" elevation={0} sx={{ 
          background: 'rgba(15, 23, 42, 0.95)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.15)',
          zIndex: 1200
        }}>
          <Toolbar>
            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <RocketLaunch sx={{ color: QUANTUM_COLORS[quantumState] }} />
                <Typography variant="h6" fontWeight="800" sx={{ color: 'white' }}>
                  Quantum Workflow Orchestrator 2.0
                </Typography>
                <Chip 
                  label={`v3.0.0`}
                  size="small"
                  sx={{ 
                    background: QUANTUM_COLORS[quantumState],
                    color: 'white',
                    fontWeight: '600'
                  }}
                />
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
                <Box sx={{ 
                  width: '10px', 
                  height: '10px', 
                  borderRadius: '50%',
                  background: QUANTUM_COLORS[quantumState],
                  animation: quantumState === QUANTUM_STATES.SUPERPOSITION ? 'pulse 2s infinite' : 'none'
                }} />
                <Typography variant="caption" sx={{ 
                  textTransform: 'uppercase', 
                  letterSpacing: '1px',
                  color: 'rgba(255, 255, 255, 0.9)'
                }}>
                  {quantumState} • {(quantumCoherence * 100).toFixed(1)}% Coherent
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Help & Tutorial">
                <IconButton 
                  onClick={() => setShowHelp(true)}
                  sx={{ color: 'white' }}
                >
                  <Help />
                </IconButton>
              </Tooltip>
              
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(e, newMode) => newMode && setViewMode(newMode)}
                size="small"
                sx={{
                  '& .MuiToggleButton-root': {
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                    color: 'rgba(255, 255, 255, 0.7)',
                    '&.Mui-selected': {
                      background: QUANTUM_COLORS.COHERENT,
                      color: 'white',
                      '&:hover': {
                        background: QUANTUM_COLORS.ENTANGLED
                      }
                    }
                  }
                }}
              >
                <ToggleButton value="dashboard">
                  <Tooltip title="Dashboard View">
                    <Dashboard />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="flow">
                  <Tooltip title="Flow View">
                    <AccountTree />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="analytics">
                  <Tooltip title="Analytics View">
                    <Analytics />
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
              
              <Tooltip title="Analyze Sequence">
                <Button
                  variant="outlined"
                  onClick={analyzeSequence}
                  startIcon={<AccountTree />}
                  sx={{
                    borderColor: QUANTUM_COLORS.ENTANGLED,
                    color: QUANTUM_COLORS.ENTANGLED
                  }}
                >
                  Sequence
                </Button>
              </Tooltip>
              
              <Tooltip title="Optimize Workflow">
                <Button
                  variant="contained"
                  onClick={optimizeWorkflow}
                  disabled={isAnalyzing}
                  startIcon={isAnalyzing ? <CircularProgress size={20} /> : <AutoAwesome />}
                  sx={{
                    background: QUANTUM_COLORS.COHERENT,
                    color: 'white',
                    '&:hover': { background: QUANTUM_COLORS.ENTANGLED },
                    '&.Mui-disabled': {
                      background: 'rgba(255, 255, 255, 0.1)'
                    }
                  }}
                >
                  {isAnalyzing ? 'Optimizing...' : 'Quantum Optimize'}
                </Button>
              </Tooltip>
            </Box>
          </Toolbar>
        </AppBar>
      )}
      
      <QuantumContainer ref={containerRef} isFullscreen={isFullscreen}>
        <QuantumBackground ref={quantumBackgroundRef}>
          <QuantumGrid />
          {renderQuantumVisualization()}
        </QuantumBackground>
        
        <Box sx={{ 
          position: 'relative', 
          zIndex: 1, 
          height: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <Box sx={{ flexGrow: 1, display: 'flex', overflow: 'hidden' }}>
            <Drawer
              variant="permanent"
              sx={{
                width: 320,
                flexShrink: 0,
                '& .MuiDrawer-paper': {
                  width: 320,
                  background: 'rgba(15, 23, 42, 0.95)',
                  borderRight: '1px solid rgba(255, 255, 255, 0.15)',
                  backdropFilter: 'blur(20px)',
                  color: 'white'
                }
              }}
            >
              <Toolbar />
              <Box sx={{ p: 3, height: '100%', overflow: 'auto' }}>
                <Typography variant="h6" fontWeight="700" sx={{ mb: 3, color: 'white' }}>
                  Lifecycle Library ({55 + customLifecycles.length})
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                  <TextField
                    fullWidth
                    placeholder="Search lifecycles..."
                    size="small"
                    sx={{ 
                      '& .MuiInputBase-root': {
                        background: 'rgba(255, 255, 255, 0.1)',
                        color: 'white',
                        '&:hover': {
                          background: 'rgba(255, 255, 255, 0.15)'
                        }
                      },
                      '& .MuiInputBase-input': {
                        color: 'white',
                        '&::placeholder': {
                          color: 'rgba(255, 255, 255, 0.6)'
                        }
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                        </InputAdornment>
                      )
                    }}
                  />
                  <Tooltip title="Add Custom Workflow">
                    <Button
                      variant="contained"
                      onClick={() => setShowAddCustomModal(true)}
                      sx={{
                        minWidth: 'auto',
                        background: QUANTUM_COLORS.SUPERPOSITION,
                        '&:hover': {
                          background: QUANTUM_COLORS.ENTANGLED
                        }
                      }}
                    >
                      <Add />
                    </Button>
                  </Tooltip>
                </Box>
                
                <Tabs 
                  value="all" 
                  sx={{ mb: 2 }}
                  TabIndicatorProps={{
                    style: {
                      backgroundColor: QUANTUM_COLORS.COHERENT
                    }
                  }}
                >
                  <Tab 
                    label="All" 
                    value="all" 
                    sx={{ 
                      color: 'rgba(255, 255, 255, 0.7)',
                      '&.Mui-selected': { color: 'white' }
                    }} 
                  />
                  <Tab 
                    label="Critical+" 
                    value="critical+" 
                    sx={{ 
                      color: 'rgba(255, 255, 255, 0.7)',
                      '&.Mui-selected': { color: 'white' }
                    }} 
                  />
                  <Tab 
                    label="Custom" 
                    value="custom" 
                    sx={{ 
                      color: 'rgba(255, 255, 255, 0.7)',
                      '&.Mui-selected': { color: 'white' }
                    }} 
                  />
                </Tabs>
                
                {customLifecycles.length > 0 && (
                  <Accordion 
                    defaultExpanded 
                    sx={{ 
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 255, 255, 0.15)',
                      mb: 2,
                      '&:before': { display: 'none' }
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMore sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
                        <Box sx={{ 
                          width: '8px', 
                          height: '8px', 
                          borderRadius: '50%',
                          background: LIFECYCLE_CATEGORY_COLORS.CUSTOM
                        }} />
                        <Typography variant="subtitle2" fontWeight="600" sx={{ color: 'white' }}>
                          Custom Workflows ({customLifecycles.length})
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0, pb: 2 }}>
                      <List dense>
                        {customLifecycles.map((lifecycle, index) => (
                          <ListItem
                            key={lifecycle.id}
                            button
                            draggable
                            onDragStart={(e) => handleDragStart(e, lifecycle)}
                            onClick={() => addLifecycleNode(lifecycle)}
                            sx={{ 
                              borderRadius: '8px', 
                              mb: 0.5,
                              background: 'rgba(255, 255, 255, 0.03)',
                              '&:hover': {
                                background: 'rgba(255, 255, 255, 0.08)'
                              }
                            }}
                          >
                            <ListItemIcon>
                              <Box sx={{ 
                                width: '32px', 
                                height: '32px', 
                                borderRadius: '8px',
                                background: LIFECYCLE_CATEGORY_COLORS.CUSTOM,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                {getLifecycleIcon('CUSTOM')}
                              </Box>
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Typography variant="body2" fontWeight="600" sx={{ color: 'white' }}>
                                  {lifecycle.name}
                                </Typography>
                              }
                              secondary={
                                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                  Custom • Impact: {(lifecycle.impactWeight * 100).toFixed(1)}%
                                </Typography>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    </AccordionDetails>
                  </Accordion>
                )}
                
                {Object.entries(
                  Object.values(LIFECYCLE_DATABASE).reduce((acc, lifecycle) => {
                    if (!acc[lifecycle.category]) acc[lifecycle.category] = [];
                    acc[lifecycle.category].push(lifecycle);
                    return acc;
                  }, {})
                ).map(([category, lifecycles]) => (
                  <Accordion 
                    key={category} 
                    sx={{ 
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 255, 255, 0.15)',
                      mb: 1,
                      '&:before': { display: 'none' }
                    }}
                  >
                    <AccordionSummary 
                      expandIcon={<ExpandMore sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
                        <Box sx={{ 
                          width: '8px', 
                          height: '8px', 
                          borderRadius: '50%',
                          background: LIFECYCLE_CATEGORY_COLORS[category] 
                        }} />
                        <Typography variant="subtitle2" fontWeight="600" sx={{ color: 'white' }}>
                          {category}
                        </Typography>
                        <Chip 
                          label={lifecycles.length} 
                          size="small" 
                          sx={{ 
                            ml: 'auto',
                            background: 'rgba(255, 255, 255, 0.15)',
                            color: 'white'
                          }}
                        />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0, pb: 2 }}>
                      <List dense>
                        {lifecycles.map(lifecycle => (
                          <ListItem
                            key={lifecycle.id}
                            button
                            draggable
                            onDragStart={(e) => handleDragStart(e, lifecycle)}
                            onClick={() => addLifecycleNode(lifecycle)}
                            sx={{ 
                              borderRadius: '8px', 
                              mb: 0.5,
                              background: 'rgba(255, 255, 255, 0.03)',
                              '&:hover': {
                                background: 'rgba(255, 255, 255, 0.08)'
                              }
                            }}
                          >
                            <ListItemIcon>
                              <Box sx={{ 
                                width: '32px', 
                                height: '32px', 
                                borderRadius: '8px',
                                background: LIFECYCLE_CATEGORY_COLORS[category],
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                {getLifecycleIcon(lifecycle.category)}
                              </Box>
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Typography variant="body2" fontWeight="600" sx={{ color: 'white' }}>
                                  {lifecycle.name}
                                </Typography>
                              }
                              secondary={
                                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                  Impact: {(lifecycle.impactWeight * 100).toFixed(1)}%
                                </Typography>
                              }
                            />
                            <Chip 
                              label={lifecycle.priority}
                              size="small"
                              sx={{ 
                                background: lifecycle.priority === 'CRITICAL+' ? 
                                  QUANTUM_COLORS.DECOHERENT :
                                  lifecycle.priority === 'CRITICAL' ? 
                                    QUANTUM_COLORS.INTERFERENCE :
                                    QUANTUM_COLORS.COHERENT,
                                color: 'white',
                                fontSize: '0.6rem',
                                fontWeight: '600'
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            </Drawer>
            
            <Box 
              sx={{ flexGrow: 1, position: 'relative' }}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              {isDraggingOver && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `rgba(${parseInt(QUANTUM_COLORS.COHERENT.slice(1, 3), 16)}, ${parseInt(QUANTUM_COLORS.COHERENT.slice(3, 5), 16)}, ${parseInt(QUANTUM_COLORS.COHERENT.slice(5, 7), 16)}, 0.1)`,
                    border: `2px dashed ${QUANTUM_COLORS.COHERENT}`,
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000,
                    pointerEvents: 'none',
                  }}
                >
                  <Box sx={{ textAlign: 'center', p: 3 }}>
                    <AutoAwesome sx={{ fontSize: 48, color: QUANTUM_COLORS.COHERENT, mb: 2 }} />
                    <Typography variant="h6" fontWeight="700" sx={{ color: 'white', mb: 1 }}>
                      Drop Lifecycle Here
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      Release to add to workflow canvas
                    </Typography>
                  </Box>
                </Box>
              )}
              
              {viewMode === 'dashboard' ? (
                renderQuantumDashboard()
              ) : (
                <ReactFlowProvider>
                  <Box sx={{ 
                    position: 'absolute', 
                    top: 0, 
                    left: 0, 
                    right: 0, 
                    bottom: 0,
                    '& .react-flow__edge-path': {
                      strokeWidth: '3 !important',
                    },
                    '& .react-flow__edge.animated path': {
                      strokeDasharray: '5 !important',
                      animation: 'dashdraw 1s linear infinite !important',
                    },
                    '@keyframes dashdraw': {
                      '0%': { strokeDashoffset: '0' },
                      '100%': { strokeDashoffset: '20' }
                    },
                    '& .react-flow__edge-textbg': {
                      fill: 'rgba(15, 23, 42, 0.9) !important',
                      stroke: 'rgba(255, 255, 255, 0.2) !important',
                      strokeWidth: '1 !important',
                    }
                  }}>
                    <FlowComponent
                      nodes={nodes}
                      edges={edges}
                      onNodesChange={onNodesChange}
                      onEdgesChange={onEdgesChange}
                      onConnect={onConnect}
                      setReactFlowInstance={setReactFlowInstance}
                      selectedNode={selectedNode}
                      setSelectedNode={setSelectedNode}
                      customLifecycles={customLifecycles}
                      generateAIRecommendations={generateAIRecommendations}
                      isAnalyzing={isAnalyzing}
                      saveWorkflow={saveWorkflow}
                      toggleFullscreen={toggleFullscreen}
                      isFullscreen={isFullscreen}
                      handleZoomIn={handleZoomIn}
                      handleZoomOut={handleZoomOut}
                      handleFitView={handleFitView}
                      reactFlowInstance={reactFlowInstance}
                      showNotification={showNotification}
                      applyAutoLayout={applyAutoLayout}
                      isAnimatingLayout={isAnimatingLayout}
                      currentLayout={currentLayout}
                      layoutEngine={layoutEngine}
                      cycleThroughLayouts={cycleThroughLayouts}
                      layoutHistory={layoutHistory}
                      undoLastLayout={undoLastLayout}
                      autoConnectWithLayout={autoConnectWithLayout}
                    />
                  </Box>
                </ReactFlowProvider>
              )}
            </Box>            
            
            <Drawer
              variant="permanent"
              anchor="right"
              sx={{
                width: 400,
                flexShrink: 0,
                '& .MuiDrawer-paper': {
                  width: 400,
                  background: 'rgba(15, 23, 42, 0.95)',
                  borderLeft: '1px solid rgba(255, 255, 255, 0.15)',
                  backdropFilter: 'blur(20px)',
                  color: 'white'
                }
              }}
            >
              <Toolbar />
              <Box sx={{ p: 3, height: '100%', overflow: 'auto' }}>
                <Tabs 
                  value={activePanel} 
                  onChange={(e, newValue) => setActivePanel(newValue)}
                  sx={{ mb: 3 }}
                  TabIndicatorProps={{
                    style: {
                      backgroundColor: QUANTUM_COLORS.COHERENT
                    }
                  }}
                >
                  <Tab 
                    label="Analytics" 
                    value="analytics" 
                    sx={{ 
                      color: 'rgba(255, 255, 255, 0.7)',
                      '&.Mui-selected': { color: 'white' }
                    }} 
                  />
                  <Tab 
                    label="Controls" 
                    value="controls" 
                    sx={{ 
                      color: 'rgba(255, 255, 255, 0.7)',
                      '&.Mui-selected': { color: 'white' }
                    }} 
                  />
                  <Tab 
                    label="Settings" 
                    value="settings" 
                    sx={{ 
                      color: 'rgba(255, 255, 255, 0.7)',
                      '&.Mui-selected': { color: 'white' }
                    }} 
                  />
                  <Tab 
                    label="Sequencing" 
                    value="sequencing" 
                    sx={{ 
                      color: 'rgba(255, 255, 255, 0.7)',
                      '&.Mui-selected': { color: 'white' }
                    }} 
                  />
                </Tabs>
                
                {activePanel === 'sequencing' && (
                  <Box>
                    <Typography variant="h6" fontWeight="700" sx={{ mb: 3, color: 'white' }}>
                      Lifecycle Sequencing
                    </Typography>
                    
                    <Card sx={{ 
                      mb: 3, 
                      background: 'rgba(255, 255, 255, 0.08)',
                      border: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      <CardContent>
                        <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 2, color: 'white' }}>
                          Sequence Analysis
                        </Typography>
                        
                        <Button
                          fullWidth
                          variant="contained"
                          onClick={analyzeSequence}
                          startIcon={<AccountTree />}
                          sx={{
                            mb: 2,
                            background: QUANTUM_COLORS.ENTANGLED,
                            '&:hover': { background: QUANTUM_COLORS.SUPERPOSITION }
                          }}
                        >
                          Analyze Sequence
                        </Button>
                        
                        {lifecycleSequence && (
                          <Box>
                            <Divider sx={{ my: 2, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
                            
                            <Grid container spacing={2}>
                              <Grid item xs={6}>
                                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                  Sequence Level
                                </Typography>
                                <Typography variant="h6" sx={{ color: QUANTUM_COLORS.COHERENT }}>
                                  {['None', 'Low', 'Medium', 'High'][lifecycleSequence.sequenceLevel]}
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                  Start Nodes
                                </Typography>
                                <Typography variant="h6" sx={{ color: 'white' }}>
                                  {lifecycleSequence.startNodes.length}
                                </Typography>
                              </Grid>
                            </Grid>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                    
                    {connectionSuggestions.length > 0 && (
                      <Card sx={{ 
                        mb: 3, 
                        background: 'rgba(255, 255, 255, 0.08)',
                        border: '1px solid rgba(255, 255, 255, 0.1)'
                      }}>
                        <CardContent>
                          <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 2, color: 'white' }}>
                            Connection Suggestions ({connectionSuggestions.length})
                          </Typography>
                          
                          <List dense>
                            {connectionSuggestions.slice(0, 5).map((suggestion, index) => (
                              <ListItem
                                key={index}
                                sx={{
                                  borderRadius: '8px',
                                  mb: 1,
                                  background: 'rgba(255, 255, 255, 0.05)'
                                }}
                              >
                                <ListItemIcon>
                                  <Link sx={{ color: QUANTUM_COLORS.ENTANGLED }} />
                                </ListItemIcon>
                                <ListItemText
                                  primary={
                                    <Typography variant="body2" sx={{ color: 'white' }}>
                                      {suggestion.sourceName} → {suggestion.targetName}
                                    </Typography>
                                  }
                                  secondary={
                                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                      {suggestion.reason} • {(suggestion.confidence * 100).toFixed(0)}% confidence
                                    </Typography>
                                  }
                                />
                              </ListItem>
                            ))}
                          </List>
                          
                          {connectionSuggestions.length > 0 && (
                            <Button
                              fullWidth
                              variant="contained"
                              onClick={autoConnectWithLayout}
                              startIcon={<AutoAwesome />}
                              disabled={isAnalyzing || nodes.length === 0}
                              sx={{
                                background: `linear-gradient(135deg, ${QUANTUM_COLORS.COHERENT} 0%, ${QUANTUM_COLORS.ENTANGLED} 100%)`,
                                color: 'white',
                                '&:hover': {
                                  background: `linear-gradient(135deg, ${QUANTUM_COLORS.ENTANGLED} 0%, ${QUANTUM_COLORS.SUPERPOSITION} 100%)`,
                                  transform: 'translateY(-2px)',
                                  boxShadow: `0 8px 20px ${QUANTUM_COLORS.ENTANGLED}80`
                                },
                                '&.Mui-disabled': {
                                  background: 'rgba(255, 255, 255, 0.1)'
                                }
                              }}
                            >
                              {isAnalyzing ? 'Creating Magic...' : 'Auto-Layout & Connect'}
                            </Button>
                          )}
                        </CardContent>
                      </Card>
                    )}
                  </Box>
                )}
               
                {activePanel === 'analytics' && (
                  <Box>
                    <Typography variant="h6" fontWeight="700" sx={{ mb: 3, color: 'white' }}>
                      Real-time Analytics
                    </Typography>
                    
                    <Card sx={{ 
                      mb: 3, 
                      background: 'rgba(255, 255, 255, 0.08)',
                      border: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      <CardContent>
                        <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 2, color: 'white' }}>
                          Performance Metrics
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', display: 'block', mb: 0.5 }}>
                              ROI Acceleration
                            </Typography>
                            <Typography variant="h5" fontWeight="800" sx={{ color: QUANTUM_COLORS.COHERENT }}>
                              {calculateROI().toFixed(1)}x
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', display: 'block', mb: 0.5 }}>
                              Time-to-Market
                            </Typography>
                            <Typography variant="h5" fontWeight="800" sx={{ color: QUANTUM_COLORS.COHERENT }}>
                              -{calculateTimeReduction().toFixed(0)}%
                            </Typography>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                    
                    <Card sx={{ 
                      mb: 3, 
                      background: 'rgba(255, 255, 255, 0.08)',
                      border: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      <CardContent>
                        <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 2, color: 'white' }}>
                          Workflow Statistics
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                              Total Nodes
                            </Typography>
                            <Typography variant="h6" sx={{ color: 'white' }}>
                              {nodes.length}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                              Total Connections
                            </Typography>
                            <Typography variant="h6" sx={{ color: 'white' }}>
                              {edges.length}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                              Custom Workflows
                            </Typography>
                            <Typography variant="h6" sx={{ color: QUANTUM_COLORS.SUPERPOSITION }}>
                              {customLifecycles.length}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                              Value Flow
                            </Typography>
                            <Typography variant="h6" sx={{ color: QUANTUM_COLORS.COHERENT }}>
                              ${calculateValueFlow().toLocaleString()}
                            </Typography>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                    
                    <Card sx={{ 
                      background: 'rgba(255, 255, 255, 0.08)',
                      border: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      <CardContent>
                        <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 2, color: 'white' }}>
                          Value Leakage Detection
                        </Typography>
                        <List dense>
                          {detectValueLeakages().slice(0, 3).map((leakage, index) => (
                            <ListItem 
                              key={index} 
                              sx={{ 
                                py: 1,
                                background: 'rgba(255, 255, 255, 0.03)',
                                borderRadius: '8px',
                                mb: 1
                              }}
                            >
                              <ListItemIcon>
                                <Warning sx={{ color: QUANTUM_COLORS.DECOHERENT }} />
                              </ListItemIcon>
                              <ListItemText
                                primary={
                                  <Typography variant="body2" sx={{ color: 'white' }}>
                                    {leakage.source}
                                  </Typography>
                                }
                                secondary={
                                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                    Loss: {leakage.amount}
                                  </Typography>
                                }
                              />
                            </ListItem>
                          ))}
                        </List>
                      </CardContent>
                    </Card>
                  </Box>
                )}
                
                {activePanel === 'controls' && (
                  <Box>
                    <Typography variant="h6" fontWeight="700" sx={{ mb: 3, color: 'white' }}>
                      Quantum Controls
                    </Typography>
                    
                    <Card sx={{ 
                      mb: 3, 
                      background: 'rgba(255, 255, 255, 0.08)',
                      border: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      <CardContent>
                        <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 2, color: 'white' }}>
                          Simulation Speed
                        </Typography>
                        <Slider
                          value={simulationSpeed}
                          onChange={(e, value) => setSimulationSpeed(value)}
                          min={0.1}
                          max={5}
                          step={0.1}
                          sx={{
                            color: QUANTUM_COLORS.COHERENT,
                            '& .MuiSlider-markLabel': {
                              color: 'rgba(255, 255, 255, 0.7)'
                            }
                          }}
                          marks={[
                            { value: 0.1, label: '0.1x' },
                            { value: 1, label: '1x' },
                            { value: 5, label: '5x' }
                          ]}
                        />
                      </CardContent>
                    </Card>
                    
                    <Card sx={{ 
                      mb: 3, 
                      background: 'rgba(255, 255, 255, 0.08)',
                      border: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      <CardContent>
                        <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 2, color: 'white' }}>
                          Visualization Settings
                        </Typography>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={quantumVisualization}
                              onChange={(e) => setQuantumVisualization(e.target.checked)}
                              sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                  color: QUANTUM_COLORS.COHERENT,
                                },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                  backgroundColor: QUANTUM_COLORS.COHERENT,
                                },
                              }}
                            />
                          }
                          label={
                            <Typography sx={{ color: 'white' }}>
                              Quantum Visualization
                            </Typography>
                          }
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              defaultChecked
                              sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                  color: QUANTUM_COLORS.COHERENT,
                                },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                  backgroundColor: QUANTUM_COLORS.COHERENT,
                                },
                              }}
                            />
                          }
                          label={
                            <Typography sx={{ color: 'white' }}>
                              Show Grid
                            </Typography>
                          }
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              defaultChecked
                              sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                  color: QUANTUM_COLORS.COHERENT,
                                },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                  backgroundColor: QUANTUM_COLORS.COHERENT,
                                },
                              }}
                            />
                          }
                          label={
                            <Typography sx={{ color: 'white' }}>
                              Auto-save
                            </Typography>
                          }
                        />
                      </CardContent>
                    </Card>
                    
                    <Card sx={{ 
                      mb: 3, 
                      background: 'rgba(255, 255, 255, 0.08)',
                      border: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      <CardContent>
                        <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 2, color: 'white' }}>
                          Quantum Layout Engine
                        </Typography>
                        
                        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                          <Button
                            fullWidth
                            variant="contained"
                            onClick={cycleThroughLayouts}
                            disabled={isAnimatingLayout || nodes.length === 0}
                            startIcon={<AutoAwesome />}
                            sx={{
                              background: QUANTUM_COLORS.SUPERPOSITION,
                              '&:hover': { background: QUANTUM_COLORS.ENTANGLED }
                            }}
                          >
                            Cycle Layouts
                          </Button>
                          
                          {layoutHistory.length > 0 && (
                            <Button
                              variant="outlined"
                              onClick={undoLastLayout}
                              disabled={isAnimatingLayout}
                              startIcon={<Refresh />}
                              sx={{
                                borderColor: QUANTUM_COLORS.INTERFERENCE,
                                color: QUANTUM_COLORS.INTERFERENCE
                              }}
                            >
                              Undo
                            </Button>
                          )}
                        </Box>
                        
                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', display: 'block', mb: 1 }}>
                          Current: {layoutEngine.generateLayoutDescription(currentLayout)}
                        </Typography>
                        
                        <Box sx={{ 
                          display: 'flex', 
                          flexWrap: 'wrap', 
                          gap: 1,
                          maxHeight: '200px',
                          overflowY: 'auto',
                          pr: 1,
                          '&::-webkit-scrollbar': {
                            width: '6px'
                          },
                          '&::-webkit-scrollbar-track': {
                            background: 'rgba(255, 255, 255, 0.1)',
                            borderRadius: '3px'
                          },
                          '&::-webkit-scrollbar-thumb': {
                            background: QUANTUM_COLORS.SUPERPOSITION,
                            borderRadius: '3px'
                          }
                        }}>
                          {Object.values(layoutEngine.layouts).map((layoutType) => (
                            <Chip
                              key={layoutType}
                              icon={<Typography sx={{ fontSize: '14px' }}>{layoutEngine.getLayoutIcon(layoutType)}</Typography>}
                              label={layoutType.replace('_', ' ')}
                              onClick={() => applyAutoLayout(layoutType)}
                              disabled={isAnimatingLayout || nodes.length === 0}
                              sx={{
                                background: currentLayout === layoutType ? 
                                  QUANTUM_COLORS.SUPERPOSITION : 
                                  'rgba(255, 255, 255, 0.1)',
                                color: 'white',
                                border: currentLayout === layoutType ? 
                                  `2px solid ${QUANTUM_COLORS.SUPERPOSITION}` : 
                                  '1px solid rgba(255, 255, 255, 0.2)',
                                '&:hover': {
                                  background: currentLayout === layoutType ? 
                                    QUANTUM_COLORS.ENTANGLED : 
                                    'rgba(255, 255, 255, 0.15)',
                                  transform: 'translateY(-2px)'
                                },
                                '&.Mui-disabled': {
                                  opacity: 0.5
                                }
                              }}
                            />
                          ))}
                        </Box>
                      </CardContent>
                    </Card>
                    
                    <Card sx={{ 
                      background: 'rgba(255, 255, 255, 0.08)',
                      border: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      <CardContent>
                        <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 2, color: 'white' }}>
                          View Controls
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <Button
                            variant="outlined"
                            startIcon={<ZoomIn />}
                            onClick={handleZoomIn}
                            size="small"
                            sx={{ borderColor: QUANTUM_COLORS.COHERENT, color: QUANTUM_COLORS.COHERENT }}
                          >
                            Zoom In
                          </Button>
                          <Button
                            variant="outlined"
                            startIcon={<ZoomOut />}
                            onClick={handleZoomOut}
                            size="small"
                            sx={{ borderColor: QUANTUM_COLORS.COHERENT, color: QUANTUM_COLORS.COHERENT }}
                          >
                            Zoom Out
                          </Button>
                          <Button
                            variant="outlined"
                            startIcon={<FitScreen />}
                            onClick={handleFitView}
                            size="small"
                            sx={{ borderColor: QUANTUM_COLORS.COHERENT, color: QUANTUM_COLORS.COHERENT }}
                          >
                            Fit View
                          </Button>
                          <Button
                            variant="outlined"
                            startIcon={isFullscreen ? <FullscreenExit /> : <Fullscreen />}
                            onClick={toggleFullscreen}
                            size="small"
                            sx={{ borderColor: QUANTUM_COLORS.SUPERPOSITION, color: QUANTUM_COLORS.SUPERPOSITION }}
                          >
                            {isFullscreen ? 'Exit Full' : 'Fullscreen'}
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Box>
                )}
                
                {activePanel === 'settings' && (
                  <Box>
                    <Typography variant="h6" fontWeight="700" sx={{ mb: 3, color: 'white' }}>
                      Settings
                    </Typography>
                    
                    <Card sx={{ 
                      mb: 3, 
                      background: 'rgba(255, 255, 255, 0.08)',
                      border: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      <CardContent>
                        <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 2, color: 'white' }}>
                          Workflow Management
                        </Typography>
                        <Button
                          fullWidth
                          variant="contained"
                          startIcon={<Add />}
                          onClick={() => setShowAddCustomModal(true)}
                          sx={{
                            mb: 2,
                            background: QUANTUM_COLORS.SUPERPOSITION,
                            '&:hover': { background: QUANTUM_COLORS.ENTANGLED }
                          }}
                        >
                          Add Custom Workflow
                        </Button>
                        <Button
                          fullWidth
                          variant="outlined"
                          startIcon={<Download />}
                          onClick={() => {
                            const data = {
                              nodes,
                              edges,
                              customLifecycles,
                              timestamp: new Date().toISOString()
                            };
                            const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `quantum-workflow-${new Date().toISOString().split('T')[0]}.json`;
                            a.click();
                            URL.revokeObjectURL(url);
                            showNotification('Workflow exported successfully!', 'success');
                          }}
                          sx={{ mb: 2, borderColor: QUANTUM_COLORS.COHERENT, color: QUANTUM_COLORS.COHERENT }}
                        >
                          Export Workflow
                        </Button>
                        <Button
                          fullWidth
                          variant="outlined"
                          startIcon={<Upload />}
                          component="label"
                          sx={{ borderColor: QUANTUM_COLORS.ENTANGLED, color: QUANTUM_COLORS.ENTANGLED }}
                        >
                          Import Workflow
                          <input
                            type="file"
                            hidden
                            accept=".json"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                const reader = new FileReader();
                                reader.onload = (event) => {
                                  try {
                                    const data = JSON.parse(event.target.result);
                                    if (data.nodes) setNodes(data.nodes);
                                    if (data.edges) setEdges(data.edges);
                                    if (data.customLifecycles) setCustomLifecycles(data.customLifecycles);
                                    showNotification('Workflow imported successfully!', 'success');
                                  } catch (error) {
                                    showNotification('Failed to import workflow', 'error');
                                  }
                                };
                                reader.readAsText(file);
                              }
                            }}
                          />
                        </Button>
                      </CardContent>
                    </Card>
                    
                    <Card sx={{ 
                      background: 'rgba(255, 255, 255, 0.08)',
                      border: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      <CardContent>
                        <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 2, color: 'white' }}>
                          Keyboard Shortcuts
                        </Typography>
                        <List dense>
                          <ListItem sx={{ py: 0.5 }}>
                            <ListItemText 
                              primary={<Typography variant="body2" sx={{ color: 'white' }}>Ctrl/Cmd + S</Typography>}
                              secondary={<Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Save workflow</Typography>}
                            />
                          </ListItem>
                          <ListItem sx={{ py: 0.5 }}>
                            <ListItemText 
                              primary={<Typography variant="body2" sx={{ color: 'white' }}>Ctrl/Cmd + O</Typography>}
                              secondary={<Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Optimize workflow</Typography>}
                            />
                          </ListItem>
                          <ListItem sx={{ py: 0.5 }}>
                            <ListItemText 
                              primary={<Typography variant="body2" sx={{ color: 'white' }}>Delete</Typography>}
                              secondary={<Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Delete selected node</Typography>}
                            />
                          </ListItem>
                          <ListItem sx={{ py: 0.5 }}>
                            <ListItemText 
                              primary={<Typography variant="body2" sx={{ color: 'white' }}>Escape</Typography>}
                              secondary={<Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Exit fullscreen</Typography>}
                            />
                          </ListItem>
                        </List>
                      </CardContent>
                    </Card>
                  </Box>
                )}
              </Box>
            </Drawer>
          </Box>
          
          <Box sx={{ 
            background: 'rgba(15, 23, 42, 0.95)',
            borderTop: '1px solid rgba(255, 255, 255, 0.15)',
            p: 1,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backdropFilter: 'blur(20px)'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  Lifecycles:
                </Typography>
                <Typography variant="caption" fontWeight="600" sx={{ color: 'white' }}>
                  {nodes.length}/{55 + customLifecycles.length}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  Connections:
                </Typography>
                <Typography variant="caption" fontWeight="600" sx={{ color: 'white' }}>
                  {edges.length}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  Value Flow:
                </Typography>
                <Typography variant="caption" fontWeight="600" sx={{ color: QUANTUM_COLORS.COHERENT }}>
                  ${calculateValueFlow().toLocaleString()}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  Custom:
                </Typography>
                <Typography variant="caption" fontWeight="600" sx={{ color: QUANTUM_COLORS.SUPERPOSITION }}>
                  {customLifecycles.length}
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Last Analysis: {formatTimeAgo(lastAnalysisRef.current)}
              </Typography>
              <CircularProgress 
                size={16} 
                thickness={4}
                value={quantumCoherence * 100}
                variant="determinate"
                sx={{ color: QUANTUM_COLORS[quantumState] }}
              />
            </Box>
          </Box>
        </Box>
        
        <SpeedDial
          ariaLabel="Quick Actions"
          sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1000 }}
          icon={<SpeedDialIcon />}
        >
          <SpeedDialAction
            icon={<Add />}
            tooltipTitle="Add Custom Workflow"
            onClick={() => setShowAddCustomModal(true)}
          />
          <SpeedDialAction
            icon={<AutoAwesome />}
            tooltipTitle="AI Recommendations"
            onClick={generateAIRecommendations}
          />
          <SpeedDialAction
            icon={<Save />}
            tooltipTitle="Save Workflow"
            onClick={saveWorkflow}
          />
          <SpeedDialAction
            icon={isFullscreen ? <FullscreenExit /> : <Fullscreen />}
            tooltipTitle={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
            onClick={toggleFullscreen}
          />
        </SpeedDial>
      </QuantumContainer>
      
      <Dialog
        open={showAddCustomModal}
        onClose={() => setShowAddCustomModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #6ee7b7 0%, #3b82f6 100%)',
          color: 'white'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Add />
            <Typography variant="h6" fontWeight="700">
              Add Custom Workflow
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ background: '#0f172a', color: 'white', pt: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Workflow Name"
                value={newCustomLifecycle.name}
                onChange={(e) => setNewCustomLifecycle({...newCustomLifecycle, name: e.target.value})}
                sx={{
                  '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                  '& .MuiInputBase-root': {
                    color: 'white',
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={2}
                value={newCustomLifecycle.description || ''}
                onChange={(e) => setNewCustomLifecycle({...newCustomLifecycle, description: e.target.value})}
                sx={{
                  '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                  '& .MuiInputBase-root': {
                    color: 'white',
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' }
                  }
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Category</InputLabel>
                <Select
                  value={newCustomLifecycle.category}
                  onChange={(e) => setNewCustomLifecycle({...newCustomLifecycle, category: e.target.value})}
                  label="Category"
                  sx={{
                    color: 'white',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' }
                  }}
                >
                  <MenuItem value="CUSTOM">Custom</MenuItem>
                  <MenuItem value="STRATEGIC">Strategic</MenuItem>
                  <MenuItem value="OPERATIONAL">Operational</MenuItem>
                  <MenuItem value="ENGINEERING">Engineering</MenuItem>
                  <MenuItem value="DIGITAL">Digital</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Impact Weight (0-1)"
                type="number"
                inputProps={{ min: 0, max: 1, step: 0.1 }}
                value={newCustomLifecycle.impactWeight}
                onChange={(e) => setNewCustomLifecycle({...newCustomLifecycle, impactWeight: parseFloat(e.target.value)})}
                sx={{
                  '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                  '& .MuiInputBase-root': {
                    color: 'white',
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Phases (comma-separated)"
                value={Array.isArray(newCustomLifecycle.phases) ? newCustomLifecycle.phases.join(', ') : ''}
                onChange={(e) => setNewCustomLifecycle({
                  ...newCustomLifecycle, 
                  phases: e.target.value.split(',').map(phase => phase.trim()).filter(phase => phase)
                })}
                sx={{
                  '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                  '& .MuiInputBase-root': {
                    color: 'white',
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' }
                  }
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ background: '#0f172a' }}>
          <Button onClick={() => setShowAddCustomModal(false)} sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Cancel
          </Button>
          <Button 
            onClick={addCustomLifecycle}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #6ee7b7 0%, #3b82f6 100%)',
              color: 'white'
            }}
            disabled={!newCustomLifecycle.name.trim()}
          >
            Create Workflow
          </Button>
        </DialogActions>
      </Dialog>
      
      <Snackbar
        open={notification.open}
        autoHideDuration={3000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity}
          sx={{ 
            background: notification.severity === 'success' ? 
              QUANTUM_COLORS.COHERENT : 
              notification.severity === 'error' ? 
                QUANTUM_COLORS.DECOHERENT :
                QUANTUM_COLORS.INTERFERENCE,
            color: 'white',
            '& .MuiAlert-icon': {
              color: 'white'
            }
          }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
      
      <Dialog
        open={showHelp}
        onClose={() => setShowHelp(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <RocketLaunch />
            <Typography variant="h6" fontWeight="700">
              Quantum Workflow Orchestrator - Getting Started
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ background: '#0f172a', color: 'white', pt: 3 }}>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, color: QUANTUM_COLORS.COHERENT }}>
              🎯 How to Use This Tool
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card sx={{ background: 'rgba(255, 255, 255, 0.05)', mb: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ color: 'white', mb: 1 }}>
                      <strong>1. Add Lifecycles</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      • Click or drag lifecycles from the left panel
                      • Use "Add Custom Workflow" for your own workflows
                      • Custom workflows work just like the 55 standard ones
                    </Typography>
                  </CardContent>
                </Card>
                
                <Card sx={{ background: 'rgba(255, 255, 255, 0.05)', mb: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ color: 'white', mb: 1 }}>
                      <strong>2. Zoom & Navigation</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      • Use zoom controls in top-right panel
                      • Press F11 or click fullscreen button
                      • Use fit view to see entire workflow
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Card sx={{ background: 'rgba(255, 255, 255, 0.05)', mb: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ color: 'white', mb: 1 }}>
                      <strong>3. Drag & Drop</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      • Drag any lifecycle to the canvas
                      • Visual drop zone will appear
                      • Release to add to workflow
                    </Typography>
                  </CardContent>
                </Card>
                
                <Card sx={{ background: 'rgba(255, 255, 255, 0.05)', mb: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ color: 'white', mb: 1 }}>
                      <strong>4. Layout Engine</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      • Try 10 different layout algorithms
                      • Auto-layout & reconnect for visual impact
                      • Cycle through layouts or pick specific ones
                      • Each layout has unique animations
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
            
            <Alert severity="info" sx={{ mt: 2, background: 'rgba(59, 130, 246, 0.1)' }}>
              <Typography variant="body2">
                <strong>Pro Tip:</strong> Use "Auto-Layout & Connect" to automatically arrange your 
                workflow with intelligent connections. The system will choose the best layout 
                and create optimal connections between nodes!
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions sx={{ background: '#0f172a' }}>
          <Button 
            onClick={() => setShowHelp(false)} 
            sx={{ 
              background: QUANTUM_COLORS.COHERENT,
              color: 'white',
              '&:hover': { background: QUANTUM_COLORS.ENTANGLED }
            }}
          >
            Got it, let's start!
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default QuantumWorkflowOrchestrator;